// ============================================================================
// COMPONENT - ComponentConfiguration.jsx
// ============================================================================
// client/src/components/ComponentConfiguration.jsx

import React, { useState, useEffect, useRef } from 'react';
import { validateComponentConfigRT } from '../../utilities/validations/validateCircuitRT';
import { CourseDomains, MULTILINE_FIELDS } from '../../types/courseDomains';
import { useComponentLibrary, useSimulator } from '../../hooks';
import RouterLogicBuilderModal from './RouterLogicBuilderModal';
import Editor from '@monaco-editor/react';

import './componentConfiguration.css';

const ComponentConfiguration = ({
  visible,
  component,
  domain,
  initialConfig,
  circuit,
  onSave,
  onClose,
  onShowValidationPanel,
  onOpenFlowEditor,
  onOpenFbdEditor,
  flowNodeTypes,     
  gatewayLabel,
  agentStateSettings,
}) => {

  const [config, setConfig] = useState({
    communicationConfigs: {},
    wirelessConfigs: {},
    pinDetails: []
  });

  const [activeTab, setActiveTab] = useState('protocols'); // 'protocols' or 'pins'
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationWarnings, setValidationWarnings] = useState([]); 
  const [isSaving, setIsSaving] = useState(false);

  const [showLogicBuilder, setShowLogicBuilder] = useState(false);
  const [logicBuilderProtocol, setLogicBuilderProtocol] = useState(null);

  // ✅ Azure IoT Hub device management
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [newDevice, setNewDevice] = useState({
    deviceId: '',
    deviceKey: '',
    enabled: true
  });
  const [flowConfig, setFlowConfig] = useState({
    flowLabel:    '',
    flowDescription: '',
    deployMode:   'full',    // 'full' | 'modified' | 'nodes'
    autoStart:    true,
    debug:        false,
  });

  const { validateComponentConfiguration, getComponentById } = useComponentLibrary();
  const { generateNodeRedFunction: generateFunction, isGenerating, generateError, setGenerateError } = useSimulator();

  // At the top of ComponentConfiguration, alongside your other refs/hooks:
  const textareaRefs = React.useRef({}); // key: `${index}-${field}` -> element

  const resizeTextarea = (el) => {
    if (!el) return;
    console.log('resizing', el, el.scrollHeight);
    el.style.height = 'auto';
    el.style.height = el.scrollHeight + 'px';
  };

  const registerTextareaRef = (key) => (el) => {
    if (el) {
      textareaRefs.current[key] = el;
      resizeTextarea(el); // resize on mount (covers reopening with existing long text)
    } else {
      delete textareaRefs.current[key];
    }
  };

  // Re-resize all registered textareas whenever the items array changes
  // (e.g. items loaded/replaced from config rather than typed in directly)
  React.useEffect(() => {
    Object.values(textareaRefs.current).forEach(resizeTextarea);
  }, [config.communicationConfigs]); // or narrow this to the specific protocol/items if you prefer

  useEffect(() => {
    if (visible && component) {
      let commConfigs = initialConfig?.communicationConfigs || component.communicationConfigs || {};

      // Migrate legacy AG_TOOL_GENERIC dataSource values (retriever/llm -> chain+invokeMode)
      if (component.componentId === 'AG_TOOL_GENERIC' && commConfigs.genericToolConfig?.settings) {
        commConfigs = {
          ...commConfigs,
          genericToolConfig: {
            ...commConfigs.genericToolConfig,
            settings: coerceGenericToolDataSource(commConfigs.genericToolConfig.settings),
          },
        };
      }

      const newConfig = {
        communicationConfigs: commConfigs,
        wirelessConfigs: initialConfig?.wirelessConfigs || component.wirelessConfigs || {},
        pinDetails: initialConfig?.pinDetails || component.pinDetails || []
      };
      
      // PLC's Protocols tab is hidden — default to FBD Program instead.
      // Every other FBD block (and everything else) still defaults to Protocols.
      setActiveTab(component.subCanvasType === 'fbd' ? 'fbd' : 'protocols');
      setConfig(newConfig);
      
      // ── NEW: initialise flow-level settings ────────────────────────────
      if (component.isFlowEngine && component.nodeRedFlow) {
        setFlowConfig({
          flowLabel:       component.nodeRedFlow.flowLabel       || '',
          flowDescription: component.nodeRedFlow.flowDescription || '',
          deployMode:      component.nodeRedFlow.deployMode      || 'full',
          autoStart:       component.nodeRedFlow.autoStart       ?? true,
          debug:           component.nodeRedFlow.debug           ?? false,
        });
      }
    }
  }, [visible, component, initialConfig]);

  // ── NEW: Send Dispatcher's sourceNode/targetNode are required-but-auto-derived
  // from FLOW_IN/TARGET_OUT wiring. Write them into config as soon as the modal
  // opens — not only when the whole sub-canvas is later saved — so this
  // component's own Save Configuration doesn't fail required-field validation
  // on a value nothing has computed yet.
  useEffect(() => {
    if (visible && component?.componentId === 'AG_GRAPH_SEND_DISPATCH') {
      const nodeNameOf = (comp) =>
        comp?.communicationConfigs?.simpleNodeConfig?.settings?.nodeName
        || comp?.communicationConfigs?.agentNodeConfig?.settings?.nodeName
        || comp?.communicationConfigs?.subgraphConfig?.settings?.nodeName
        || comp?.label
        || comp?.componentName;

      const incomingWire = circuit?.connections?.find(c =>
        c.to?.componentInstanceId === component.instanceId && c.to?.pinName === 'FLOW_IN'
      );
      const sourceComp = incomingWire
        ? circuit?.components?.find(c => c.instanceId === incomingWire.from.componentInstanceId)
        : null;

      const outgoingWire = circuit?.connections?.find(c =>
        c.from?.componentInstanceId === component.instanceId && c.from?.pinName === 'TARGET_OUT'
      );
      const targetComp = outgoingWire
        ? circuit?.components?.find(c => c.instanceId === outgoingWire.to.componentInstanceId)
        : null;

      setConfig(prev => ({
        ...prev,
        communicationConfigs: {
          ...prev.communicationConfigs,
          sendDispatchConfig: {
            ...prev.communicationConfigs?.sendDispatchConfig,
            settings: {
              ...prev.communicationConfigs?.sendDispatchConfig?.settings,
              sourceNode: sourceComp ? nodeNameOf(sourceComp) : '',
              targetNode: targetComp ? nodeNameOf(targetComp) : '',
            },
          },
        },
      }));
    }
  }, [visible, component, circuit]);

  if (!visible || !component) return null;
  console.log('wirelessConfigs:', component.wirelessConfigs);

  const domainConfig = CourseDomains[domain]?.configModal ?? CourseDomains['IoT'].configModal;

  function isMultilineField(settingKey) {
    return MULTILINE_FIELDS.includes(settingKey);
  }

  // Near other constants, above the component or inside it
  const WIRELESS_FIELD_LABELS = {
    WiFi: {
      nodeAddress: 'IP Address',
      peerAddress: 'Peer IP Address',
      staticIP:    'Static IP Address',
      dhcp:        'Enable DHCP',
      gateway:     'Gateway',
      subnet:      'Subnet Mask',
      hostname:    'Hostname',
      role:     'Role (TX / RX / Other)',
      protocol: 'Transmission Protocol',
    },
    LoRa: {
      nodeAddress: 'Node Address',
      peerAddress: 'Peer Address',
    },
    // other wireless types fall through to the generic labeler
  };
  function getWirelessFieldLabel(wirelessType, settingKey) {
    return (
      WIRELESS_FIELD_LABELS[wirelessType]?.[settingKey] ??
      settingKey.replace(/([A-Z])/g, ' $1').trim()
    );
  }

  // Declarative mode/type-conditional field visibility, keyed by componentId.
  // A listed field only renders while `controllingField`'s current value is
  // included in that field's allowed-values array. Fields not listed here
  // are always visible (unconditional, same as before this fix).
  const CONDITIONAL_FIELD_VISIBILITY = {
    AG_FOUND_PARSER: {
      controllingField: 'parserType',
      fields: {
        pydanticModelName: ['pydantic'],
        pydanticFields:    ['pydantic'],
      },
    },
    AG_CHAIN_LCEL: {
      controllingField: 'runnableMode',
      fields: {
        contextTemplate: ['retrieval'],
        contextKey:      ['retrieval'],
        inputKey:        ['retrieval'],
        topK:            ['retrieval'],
      },
    },
    AG_RAG_PINECONE: {
      controllingField: 'mode',
      fields: {
        metric: ['retrieval'],
      },
    },
    AG_CHAIN_RETRIEVAL: {
      controllingField: 'retrievalMode',
      fields: {
        contextTemplate: ['retrieval'],
        contextKey:      ['retrieval'],
        inputKey:        ['retrieval'],
      },
    },
    // AG_TOOL_GENERIC: {
    //   controllingField: 'dataSource',
    //   fields: {
    //     primaryArg:     ['retriever', 'state'],
    //     promptTemplate: ['llm'],
    //   },
    // },
    AG_TOOL_GENERIC: {
      controllingField: 'dataSource', // still used for the simple invokeMode case
      fields: {
        invokeMode:     ['chain'],
        primaryArg:     (live) =>
          live.dataSource === 'state' ||
          (live.dataSource === 'chain' && live.invokeMode === 'single_string'),
        promptTemplate: (live) =>
          live.dataSource === 'chain' && live.invokeMode === 'messages',
      },
    },
  };

  // function isFieldVisible(componentId, settingKey, liveSettings) {
  //   const rule = CONDITIONAL_FIELD_VISIBILITY[componentId];
  //   if (!rule) return true; // no conditional rules for this component
  //   const allowedValues = rule.fields[settingKey];
  //   if (!allowedValues) return true; // this field isn't conditional
  //   return allowedValues.includes(liveSettings[rule.controllingField]);
  // }

  // AFTER — backward compatible with every other component's array rules
  
  function isFieldVisible(componentId, settingKey, liveSettings) {
    const rule = CONDITIONAL_FIELD_VISIBILITY[componentId];
    if (!rule) return true;
    const fieldRule = rule.fields[settingKey];
    if (fieldRule === undefined) return true;
    if (typeof fieldRule === 'function') return fieldRule(liveSettings);
    return fieldRule.includes(liveSettings[rule.controllingField]);
  }
  
  function getWiredSourceForPin(pinName) {
    console.log('🔍 getWiredSourceForPin called:', { 
      pinName, 
      hasCircuit: !!circuit, 
      hasConnections: !!circuit?.connections, 
      hasComponents: !!circuit?.components,
      connectionsLength: circuit?.connections?.length,
      componentsLength: circuit?.components?.length,
    });

    if (!circuit?.connections || !circuit?.components) return null;
    const conn = circuit.connections.find(c =>
      (c.to.componentInstanceId === component.instanceId && c.to.pinName === pinName) ||
      (c.from.componentInstanceId === component.instanceId && c.from.pinName === pinName)
    );
    if (!conn) return null;
    console.log('🔌 conn found:', conn);

    const isIncoming    = conn.to.componentInstanceId === component.instanceId;
    const otherInstanceId = isIncoming ? conn.from.componentInstanceId : conn.to.componentInstanceId;
    const otherPinName    = isIncoming ? conn.from.pinName             : conn.to.pinName;
    const otherComp = circuit.components.find(c => c.instanceId === otherInstanceId);
    if (!otherComp) return null;
    console.log('🔍 otherComp:', {
      name: otherComp?.componentName,
      category: otherComp?.category,
      nodeRedType: otherComp?.nodeRedType,
      componentId: otherComp?.componentId,
      commConfigKeys: Object.keys(otherComp?.communicationConfigs || {}),
      func: Object.values(otherComp?.communicationConfigs || {})
        .map(cfg => cfg?.settings?.func)
        .find(f => typeof f === 'string' && f.length > 0),
      quantity: Object.values(otherComp?.communicationConfigs || {})
        .map(cfg => cfg?.settings)
        .find(s => s && typeof s.quantity === 'number'),
    });

    const isModbusRead =
      otherComp.componentId === 'NR_IN_MODBUS_001' && otherPinName === 'NR_OUT_DATA';

    const isFunctionNode =
      otherComp.category === 'nr_processing' &&
      otherComp.nodeRedType === 'function';

    // ── Modbus Read: generate payload[0]…payload[N-1] from register quantity ──
    if (isModbusRead) {
      const modbusSettings = Object.values(otherComp.communicationConfigs || {})
        .map(cfg => cfg?.settings)
        .find(s => s && typeof s.quantity === 'number');

      const quantity = modbusSettings?.quantity ?? 1;
      const suggestedFields = Array.from({ length: quantity }, (_, i) => `payload[${i}]`);

      return {
        label:           otherComp.label || otherComp.componentName,
        nodeRedType:     otherComp.nodeRedType,
        pinName:         otherPinName,
        addressing:      'index',
        exampleField:    'payload[0]',
        suggestedFields,
      };
    }

    // ── Function node: regex-scan func string for msg.payload.X = assignments ──
    if (isFunctionNode) {
      const funcString = Object.values(otherComp.communicationConfigs || {})
        .map(cfg => cfg?.settings?.func)
        .find(f => typeof f === 'string' && f.length > 0) ?? '';

      const PAYLOAD_ASSIGN_RE = /msg\.payload\.([a-zA-Z_$][a-zA-Z0-9_$]*)\s*=/g;
      const found = new Set();
      let match;
      while ((match = PAYLOAD_ASSIGN_RE.exec(funcString)) !== null) {
        found.add(match[1]);
      }

      console.log('🔍 func scan result:', { found: Array.from(found), funcSnippet: funcString.slice(0, 300) });  // ← ADD HERE

      const suggestedFields = found.size > 0
        ? Array.from(found).map(key => `payload.${key}`)
        : [];

      return {
        label:           otherComp.label || otherComp.componentName,
        nodeRedType:     otherComp.nodeRedType,
        pinName:         otherPinName,
        addressing:      'key',
        exampleField:    suggestedFields[0] ?? 'payload',
        suggestedFields,
      };
    }

    // ── Everything else (MQTT In, Inject, HTTP, etc.) ──
    return {
      label:           otherComp.label || otherComp.componentName,
      nodeRedType:     otherComp.nodeRedType,
      pinName:         otherPinName,
      addressing:      'key',
      exampleField:    'payload',
      suggestedFields: [],
    };
  };

  function getAgentStateFieldOptions() {
    const stateSettings = agentStateSettings;
    if (!stateSettings) return [];
    const rawFields = stateSettings.fields;
    const rows = Array.isArray(rawFields)
      ? rawFields.map(f => (typeof f === 'string' ? { name: f, type: 'string' } : { name: f?.name, type: f?.type || 'string' }))
      : [];
    const extra = [
      ...(stateSettings.includeSessionId ? [{ name: 'session_id', type: 'string' }] : []),
      ...(stateSettings.includeUserId    ? [{ name: 'user_id',    type: 'string' }] : []),
    ];
    return [...rows.filter(f => f.name), ...extra];
  }

  function getConditionalEdgeBranchLabels() {
    const branchWires = circuit?.connections?.filter(c =>
      c.from?.componentInstanceId === component.instanceId && c.from?.pinName === 'BRANCH_OUT'
    ) || [];
    return branchWires.map(w => w.label).filter(Boolean);
  }

  function generateSimulatedIP() {
    // Mirrors the simulated-IP convention already used server-side
    // (simulatorServices.ts startContinuousSimulation / connectWiFi).
    return '192.168.1.' + (100 + Math.floor(Math.random() * 50));
  }

  // Get available protocols from component
  const availableProtocols =
  (component.communicationCapabilities?.length > 0)
    ? component.communicationCapabilities
    : Object.keys(component.communicationConfigs || {});
  const protocolConfigs = component.communicationConfigs || {};

  // Get software pins from pinDetails
  const availablePins = {};
  if (component.pinDetails) {
    
    component.pinDetails.forEach((pin, index) => {
      
      if (pin.isSoftwarePin) {
        console.log('✅ Found software pin:', pin.pinName);
        availablePins[pin.pinName] = {
          enabled: pin.enabled || false,
          type: pin.softwarePinType,
          direction: pin.direction,
          description: pin.description,
          settings: pin.settings || {},
          validationRules: pin.validationRules || {}
        };
      }
    });
    
    console.log('Final availablePins:', availablePins);
    console.log('Object.keys(availablePins).length:', Object.keys(availablePins).length);
  }

  const IPV4_PATTERN = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/;
  function ipFieldError(value) {
    if (!value) return null; // empty is allowed (placeholder token case)
    if (value.startsWith('{') && value.endsWith('}')) return null; // {PEER_IP_ADDRESS} placeholder token
    const match = value.match(IPV4_PATTERN);
    if (!match) return 'Format must be nnn.nnn.nnn.nnn';
    const outOfRange = match.slice(1).some(octet => Number(octet) > 255);
    if (outOfRange) return 'Each segment must be 0–255';
    return null;
  }

  // ✅ Detect if this is Azure IoT Hub component (has sdkType setting)
  const isAzureIoTHub = component.category === 'azure_iot' && 
                        availableProtocols.some(p => p === 'azureIoT');
  console.log('🔍 Is Azure IoT Hub:', isAzureIoTHub);

  const WIFI_FIELD_ORDER = [
    'ssid',
    'password',
    'hostname',
    'channel',
    'mode',
    'autoReconnect',
    'gateway',
    'subnet',
    'dhcp',
    'staticIP',     // last but one
    'nodeAddress',  // last — "IP Address"
    // peerAddress goes wherever makes sense for the lab; if it must also be
    // last, append it after nodeAddress
    'peerAddress',
  ];

  const LORA_FIELD_ORDER = [
    'nodeAddress',
    'peerAddress',
    'frequency',
    'spreadingFactor',
    'bandwidth',
    'codingRate',
    'syncWord',
    'txPowerLoRa',
  ];

  const WIFI_ROLE_OPTIONS = ['TX', 'RX', 'TX/RX', 'None'];
  const WIFI_PROTOCOL_OPTIONS = ['HTTP', 'WebSocket', 'UDP', 'TCP'];
    
  // Handle protocol enable/disable
  const handleProtocolToggle = (protocolName) => {
    setConfig(prev => ({
      ...prev,
      communicationConfigs: {
        ...prev.communicationConfigs,
        [protocolName]: {
          ...prev.communicationConfigs[protocolName],
          enabled: !prev.communicationConfigs[protocolName]?.enabled,
          settings: prev.communicationConfigs[protocolName]?.settings || 
                   protocolConfigs[protocolName]?.settings || {}
        }
      }
    }));
  };

  // Handle protocol setting change
  const handleProtocolSettingChange = (protocolName, settingKey, value) => {
    setConfig(prev => ({
      ...prev,
      communicationConfigs: {
        ...prev.communicationConfigs,
        [protocolName]: {
          ...prev.communicationConfigs[protocolName],
          settings: {
            ...prev.communicationConfigs[protocolName]?.settings,
            [settingKey]: value
          }
        }
      }
    }));
  };

  // Handle wireless enable/disable
  const handleWirelessToggle = (wirelessType) => {
    setConfig(prev => {
      const currentSettings = prev.wirelessConfigs?.[wirelessType]?.settings ||
                              component.wirelessConfigs?.[wirelessType]?.settings || {};
      const turningOn = !prev.wirelessConfigs?.[wirelessType]?.enabled;

      // If we're enabling this wireless type, DHCP is on, and there's no
      // nodeAddress yet, generate one now instead of waiting for the DHCP
      // checkbox to be toggled (it may never be toggled if it's already on).
      const dhcpOn = currentSettings.dhcp ?? true;
      const needsInitialAddress = turningOn && dhcpOn && !currentSettings.nodeAddress;

      return {
        ...prev,
        wirelessConfigs: {
          ...prev.wirelessConfigs,
          [wirelessType]: {
            enabled: turningOn,
            settings: needsInitialAddress
              ? { ...currentSettings, nodeAddress: generateSimulatedIP() }
              : currentSettings,
          }
        }
      };
    });
  };

  // Handle wireless setting change
  const handleWirelessSettingChange = (wirelessType, settingKey, value) => {
    setConfig(prev => ({
      ...prev,
      wirelessConfigs: {
        ...prev.wirelessConfigs,
        [wirelessType]: {
          ...prev.wirelessConfigs[wirelessType],
          settings: {
            ...prev.wirelessConfigs[wirelessType]?.settings,
            [settingKey]: value
          }
        }
      }
    }));
  };

  // Render wireless settings form
  const renderWirelessSettings = (wirelessType, wirelessConfig) => {
    const settings = wirelessConfig.settings || {};
    const isEnabled = config.wirelessConfigs?.[wirelessType]?.enabled || false;
    if (!isEnabled) return null;

    if (Object.keys(settings).length === 0) {
      return <div className="config-empty">No settings available</div>;
    }

    const normalizedType = wirelessType.toLowerCase();

    if (normalizedType === 'wifi') {
      return renderWifiSettings(wirelessConfig, wirelessType);
    }

    if (normalizedType === 'lora') {
      return renderLoraSettings(wirelessConfig, wirelessType);
    }

    const orderedKeys = [
      ...LORA_FIELD_ORDER.filter(k => k in settings),
      ...Object.keys(settings).filter(k => !LORA_FIELD_ORDER.includes(k)),
    ];

    return (
      <div className="config-settings-form">
        {/* {Object.keys(settings).map(settingKey => { */}
        {orderedKeys.map(settingKey => {
          const value = config.wirelessConfigs?.[wirelessType]?.settings?.[settingKey] ?? settings[settingKey];
          const settingType = typeof settings[settingKey];
          
          return (
            <div key={settingKey} className="config-form-group">
              <label className="config-label">
                {/* {settingKey.replace(/([A-Z])/g, ' $1').trim()}: */}
                {getWirelessFieldLabel(wirelessType, settingKey)}:
              </label>
              {settingType === 'boolean' ? (
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, e.target.checked)}
                  className="config-checkbox"
                />
              ) : settingType === 'number' ? (
                <input
                  type="number"
                  value={value}
                  onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, parseInt(e.target.value))}
                  className="config-input"
                />
              ) : Array.isArray(value) ? (
                <input
                  type="text"
                  value={value.join(', ')}
                  onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, e.target.value.split(',').map(s => s.trim()))}
                  className="config-input"
                  placeholder="Comma-separated values"
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, e.target.value)}
                  className="config-input"
                  placeholder={`Enter ${settingKey}`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWifiSettings = (wirelessConfig, wirelessType) => {
    const settings = wirelessConfig.settings || {};
    const liveSettings = config.wirelessConfigs?.[wirelessType]?.settings || {};
    const dhcpEnabled = liveSettings.dhcp ?? settings.dhcp ?? true;

    const groups = [
      { title: 'Network Credentials', icon: '🔑', keys: ['ssid', 'password', 'hostname'] },
      { title: 'Connection Behavior', icon: '⚙️', keys: ['mode', 'channel', 'autoReconnect'] },
      { title: 'Addressing', icon: '🌐', keys: ['dhcp', 'gateway', 'subnet', 'staticIP', 'nodeAddress'] },
      { title: 'Lab Peer Pairing', icon: '🔗', keys: ['peerAddress'] },
      { title: 'Lab Role & Protocol', icon: '🔁', keys: ['role', 'protocol'], alwaysShow: true },
    ];

    const renderField = (settingKey) => {
      const value = liveSettings[settingKey] ?? settings[settingKey];
      const label = getWirelessFieldLabel('WiFi', settingKey); // label lookup can stay 'WiFi' since that's just your label map's key

      if (settingKey === 'nodeAddress') {
        const derivedValue = dhcpEnabled
          ? (liveSettings.nodeAddress || '(assigned automatically when run)')
          : (liveSettings.staticIP || '(enter Static IP Address below)');
        return (
          <div key={settingKey} className="config-form-group">
            <label className="config-label">{label}:</label>
            <input
              type="text"
              className="config-input"
              value={derivedValue}
              readOnly
              style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
            />
            <small className="config-hint">
              {dhcpEnabled ? 'Assigned automatically — DHCP is enabled' : 'Mirrors the Static IP Address below'}
            </small>
          </div>
        );
      }

      if (settingKey === 'staticIP') {
        return (
          <div key={settingKey} className="config-form-group">
            <label className="config-label">{label}:</label>
            <input
              type="text"
              className="config-input"
              value={liveSettings.staticIP ?? ''}
              disabled={dhcpEnabled}
              onChange={(e) => {
                const newStaticIP = e.target.value;
                handleWirelessSettingChange(wirelessType, 'staticIP', newStaticIP);
                handleWirelessSettingChange(wirelessType, 'nodeAddress', newStaticIP);
              }}
              style={dhcpEnabled ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
              placeholder="nnn.nnn.nnn.nnn"
            />
            {!dhcpEnabled && ipFieldError(liveSettings.staticIP) && (
              <small className="config-hint" style={{ color: '#ef4444' }}>{ipFieldError(liveSettings.staticIP)}</small>
            )}
          </div>
        );
      }

      if (settingKey === 'dhcp') {
        return (
          <div key={settingKey} className="config-form-group">
            <label className="config-label">{label}:</label>
            <input
              type="checkbox"
              className="config-checkbox"
              checked={dhcpEnabled}
              onChange={(e) => {
                const nextDhcp = e.target.checked;
                handleWirelessSettingChange(wirelessType, 'dhcp', nextDhcp);
                if (nextDhcp) {
                  handleWirelessSettingChange(wirelessType, 'nodeAddress', generateSimulatedIP());
                } else {
                  handleWirelessSettingChange(wirelessType, 'nodeAddress', liveSettings.staticIP || '');
                }
              }}
            />
            <small className="config-hint">
              When enabled, the IP Address is assigned automatically.
              When disabled, set the Static IP Address manually.
            </small>
          </div>
        );
      }

      if (settingKey === 'role') {
        const liveRole = liveSettings.role ?? '';
        return (
          <div key={settingKey} className="config-form-group">
            <label className="config-label">{label}:</label>
            <select
              className="config-input"
              value={liveRole}
              onChange={(e) => handleWirelessSettingChange(wirelessType, 'role', e.target.value)}
            >
              <option value="">Select role…</option>
              {WIFI_ROLE_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        );
      }

      if (settingKey === 'protocol') {
        const liveRole = liveSettings.role ?? '';
        if (!liveRole || liveRole === 'None') return null; // nothing to configure without a role
        const liveProtocol = liveSettings.protocol ?? '';
        return (
          <div key={settingKey} className="config-form-group">
            <label className="config-label">{label}:</label>
            <select
              className="config-input"
              value={liveProtocol}
              onChange={(e) => handleWirelessSettingChange(wirelessType, 'protocol', e.target.value)}
            >
              <option value="">Select protocol…</option>
              {WIFI_PROTOCOL_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        );
      }

      const isIpShaped = settingKey === 'peerAddress';
      return (
        <div key={settingKey} className="config-form-group">
          <label className="config-label">{label}:</label>
          {typeof settings[settingKey] === 'boolean' ? (
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, e.target.checked)}
              className="config-checkbox"
            />
          ) : (
            <input
              type="text"
              className="config-input"
              value={value}
              placeholder={isIpShaped ? 'nnn.nnn.nnn.nnn' : `Enter ${label}`}
              onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, e.target.value)}
            />
          )}
          {isIpShaped && ipFieldError(value) && (
            <small className="config-hint" style={{ color: '#ef4444' }}>{ipFieldError(value)}</small>
          )}
        </div>
      );
    };

    return (
      <>
        {groups.map(group => {
          const presentKeys = group.alwaysShow ? group.keys : group.keys.filter(k => k in settings);
          // group.alwaysShow ? group.keys : group.keys.filter(k => k in settings)
          if (presentKeys.length === 0) return null;
          return (
            <div key={group.title} className="config-settings-group">
              <div className="config-settings-group-title">
                <span className="group-icon">{group.icon}</span>
                {group.title}
              </div>
              <div className="config-settings-form">
                {presentKeys.map(renderField)}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  const renderLoraSettings = (wirelessConfig, wirelessType) => {
    const settings = wirelessConfig.settings || {};
    const liveSettings = config.wirelessConfigs?.[wirelessType]?.settings || {};

    const groups = [
      { title: 'Lab Peer Pairing', icon: '🔗', keys: ['nodeAddress', 'peerAddress'] },
      { title: 'Radio Parameters', icon: '📡', keys: ['frequency', 'spreadingFactor', 'bandwidth', 'codingRate', 'syncWord', 'txPowerLoRa'] },
    ];

    const renderField = (settingKey) => {
      const value = liveSettings[settingKey] ?? settings[settingKey];
      const label = getWirelessFieldLabel('LoRa', settingKey); // label-map lookup key stays 'LoRa' — that's just WIRELESS_FIELD_LABELS' own key, unrelated to your data's casing
      const settingType = typeof settings[settingKey];

      return (
        <div key={settingKey} className="config-form-group">
          <label className="config-label">{label}:</label>
          {settingType === 'boolean' ? (
            <input
              type="checkbox"
              checked={value}
              onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, e.target.checked)}
              className="config-checkbox"
            />
          ) : settingType === 'number' ? (
            <input
              type="number"
              className="config-input"
              value={value}
              onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, parseInt(e.target.value))}
            />
          ) : (
            <input
              type="text"
              className="config-input"
              value={value}
              placeholder={`Enter ${label}`}
              onChange={(e) => handleWirelessSettingChange(wirelessType, settingKey, e.target.value)}
            />
          )}
        </div>
      );
    };

    return (
      <>
        {groups.map(group => {
          const presentKeys = group.keys.filter(k => k in settings);
          if (presentKeys.length === 0) return null;
          return (
            <div key={group.title} className="config-settings-group">
              <div className="config-settings-group-title">
                <span className="group-icon">{group.icon}</span>
                {group.title}
              </div>
              <div className="config-settings-form">
                {presentKeys.map(renderField)}
              </div>
            </div>
          );
        })}
      </>
    );
  };

  // Handle software pin enable/disable
  const handlePinToggle = (pinName) => {
    // ✅ Get the original pin from component to read warnings/restrictions
    const originalPin = component.pinDetails?.find(p => p.pinName === pinName);
    const currentPin = config.pinDetails.find(p => p.pinName === pinName);
    const isCurrentlyEnabled = currentPin?.enabled || false;

    // ✅ Check restrictions BEFORE toggling
    if (!isCurrentlyEnabled && originalPin?.restrictions?.length > 0) {
      // Block enabling if restricted
      onShowValidationPanel({
        hasErrors: true,
        errors: originalPin.restrictions.map(r => `${pinName}: Restricted - ${r}`),
        warnings: []
      });
      return; // Don't toggle
    }

    setConfig(prev => {
      const updatedPinDetails = prev.pinDetails.map(pin => {
        if (pin.pinName === pinName) {
          return {
            ...pin,
            enabled: !pin.enabled,
            settings: pin.settings || availablePins[pinName]?.settings || {}
          };
        }
        return pin;
      });

      return {
        ...prev,
        pinDetails: updatedPinDetails
      };
    });

    // ✅ Show warnings when enabling (not disabling)
    // if (!isCurrentlyEnabled && originalPin?.warnings?.length > 0) {
    //   onShowValidationPanel({
    //     hasErrors: false,
    //     errors: [],
    //     warnings: originalPin.warnings.map(w => ({
    //       protocol: pinName,
    //       message: w,
    //       suggestion: null
    //     }))
    //   });
    // }
  };

  // Handle software pin setting change
  const handlePinSettingChange = (pinName, settingKey, value) => {
    setConfig(prev => {
      const updatedPinDetails = prev.pinDetails.map(pin => {
        if (pin.pinName === pinName) {
          return {
            ...pin,
            settings: {
              ...pin.settings,
              [settingKey]: value
            }
          };
        }
        return pin;
      });

      return {
        ...prev,
        pinDetails: updatedPinDetails
      };
    });
  };

  // Validate configuration
  const validateConfig = () => {
    // Use the shared validation helper
    const validation = validateComponentConfigRT(component, config, circuit);
    
    return {
      errors: validation.errors.map(err => {
        if (err.protocol) {
          return `${err.protocol}: ${err.message}`;
        } else if (err.pin) {
          return `${err.pin}: ${err.message}`;
        }
        return err.message;
      }),
      warnings: validation.warnings.map(warn => ({
        protocol: warn.protocol,
        message: warn.message,
        suggestion: warn.suggestion
      }))
    };
  };

  // Handle save
  const handleSave = async () => {

    // ── IP format pre-check for WiFi (case-insensitive key lookup) ──────────
    const wifiKey = Object.keys(config.wirelessConfigs || {}).find(
      k => k.toLowerCase() === 'wifi'
    );

    if (wifiKey && config.wirelessConfigs[wifiKey]?.enabled) {
      const wifiSettings = config.wirelessConfigs[wifiKey].settings || {};
      const dhcpOn = wifiSettings.dhcp ?? true;
      const fieldsToCheck = dhcpOn
        ? ['peerAddress']
        : ['nodeAddress', 'staticIP', 'peerAddress'];

      const ipErrors = fieldsToCheck
        .map(key => {
          const err = ipFieldError(wifiSettings[key]);
          return err ? `${getWirelessFieldLabel('WiFi', key)}: ${err}` : null;
        })
        .filter(Boolean);

      if (ipErrors.length > 0) {
        onShowValidationPanel({ hasErrors: true, errors: ipErrors, warnings: [] });
        return;
      }
    }

    // ============================================================
    // STEP 1: Frontend validation (instant, no API call)
    // ============================================================
    const validation = validateConfig();

    if (validation.errors.length > 0) {
      onShowValidationPanel({
        hasErrors: true,
        errors: validation.errors,
        warnings: validation.warnings
      });
      return;
    }

    // ============================================================
    // STEP 2: Backend validation (API call)
    // ============================================================
    setIsSaving(true);

    try {
      const backendValidation = await validateComponentConfiguration(
        component.componentId,
        config
      );

      // Backend returned errors
      if (!backendValidation.success || !backendValidation.data?.valid) {
        const backendErrors = backendValidation.data?.errors || ['Backend validation failed'];
        const backendWarnings = backendValidation.data?.warnings || [];

        // ✅ Merge with any frontend warnings
        onShowValidationPanel({
          hasErrors: true,
          errors: backendErrors,
          warnings: [...validation.warnings, ...backendWarnings]
        });
        return;
      }

      // ============================================================
      // STEP 3: Save
      // ============================================================
      onSave(config, flowConfig);

      // Show warnings if any (frontend + backend combined)
      const allWarnings = [
        ...validation.warnings,
        ...(backendValidation.data?.warnings || [])
      ];

      if (allWarnings.length > 0) {
        onClose();
        onShowValidationPanel({
          hasErrors: false,
          errors: [],
          warnings: allWarnings
        });
        return;
      }

      // No errors, no warnings - close immediately
      onClose();

    } catch (error) {
      console.error('❌ Backend validation error:', error);
      onShowValidationPanel({
        hasErrors: true,
        errors: [`Validation service error: ${error.message}`],
        warnings: []
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Add this new function (around line 290, near handleSave):
  const handleClose = () => {
    setValidationErrors([]);
    setValidationWarnings([]);
    onClose();
  };

  // ============================================================
  // Azure IoT Hub Device Management Handlers
  // ============================================================

  // Add new device or update existing device
  const handleAddDevice = () => {
    if (!newDevice.deviceId || !newDevice.deviceKey) {
      alert('Device ID and Device Key are required');
      return;
    }

    // Validate device ID format
    const deviceIdPattern = /^[a-zA-Z0-9-:.+%_#*?!()=@;$']+$/;
    if (!deviceIdPattern.test(newDevice.deviceId)) {
      alert('Invalid Device ID format. Use alphanumeric and special characters only.');
      return;
    }

    // Validate device key is Base64
    const base64Pattern = /^[A-Za-z0-9+/=]+$/;
    if (!base64Pattern.test(newDevice.deviceKey)) {
      alert('Invalid Device Key. Must be Base64 encoded.');
      return;
    }

    setConfig(prev => {
      const currentDevices = prev.communicationConfigs?.azureIoT?.settings?.devices || [];
      
      // EDIT MODE: Update existing device
      if (editingDevice) {
        const updatedDevices = currentDevices.map(d => 
          d.deviceId === editingDevice.deviceId 
            ? { ...newDevice }
            : d
        );

        return {
          ...prev,
          communicationConfigs: {
            ...prev.communicationConfigs,
            azureIoT: {
              ...prev.communicationConfigs.azureIoT,
              settings: {
                ...prev.communicationConfigs.azureIoT?.settings,
                devices: updatedDevices
              }
            }
          }
        };
      }
      
      // ADD MODE: Check for duplicate device ID
      if (currentDevices.some(d => d.deviceId === newDevice.deviceId)) {
        alert('Device ID already exists');
        return prev;
      }

      // ADD MODE: Add new device
      return {
        ...prev,
        communicationConfigs: {
          ...prev.communicationConfigs,
          azureIoT: {
            ...prev.communicationConfigs.azureIoT,
            settings: {
              ...prev.communicationConfigs.azureIoT?.settings,
              devices: [...currentDevices, { ...newDevice }]
            }
          }
        }
      };
    });

    // Reset form
    setNewDevice({ deviceId: '', deviceKey: '', enabled: true });
    setEditingDevice(null);
    setShowDeviceModal(false);
  };

  // Edit existing device
  const handleEditDevice = (device) => {
    setEditingDevice(device);
    setNewDevice({
      deviceId: device.deviceId,
      deviceKey: device.deviceKey,
      enabled: device.enabled
    });
    setShowDeviceModal(true);
  };

  // Remove device from devices array
  const handleRemoveDevice = (deviceId) => {
    if (!window.confirm(`Remove device "${deviceId}"?`)) return;

    setConfig(prev => {
      const currentDevices = prev.communicationConfigs?.azureIoT?.settings?.devices || [];
      const updatedDevices = currentDevices.filter(d => d.deviceId !== deviceId);

      // If removed device was active, clear activeDeviceId
      const wasActive = prev.communicationConfigs?.azureIoT?.settings?.activeDeviceId === deviceId;

      return {
        ...prev,
        communicationConfigs: {
          ...prev.communicationConfigs,
          azureIoT: {
            ...prev.communicationConfigs.azureIoT,
            settings: {
              ...prev.communicationConfigs.azureIoT?.settings,
              devices: updatedDevices,
              activeDeviceId: wasActive ? '' : prev.communicationConfigs?.azureIoT?.settings?.activeDeviceId
            }
          }
        }
      };
    });
  };

  // Toggle device enabled/disabled
  const handleToggleDevice = (deviceId) => {
    setConfig(prev => {
      const currentDevices = prev.communicationConfigs?.azureIoT?.settings?.devices || [];
      const updatedDevices = currentDevices.map(d => 
        d.deviceId === deviceId ? { ...d, enabled: !d.enabled } : d
      );

      return {
        ...prev,
        communicationConfigs: {
          ...prev.communicationConfigs,
          azureIoT: {
            ...prev.communicationConfigs.azureIoT,
            settings: {
              ...prev.communicationConfigs.azureIoT?.settings,
              devices: updatedDevices
            }
          }
        }
      };
    });
  };

  // Set active device for code generation
  const handleSetActiveDevice = (deviceId) => {
    setConfig(prev => ({
      ...prev,
      communicationConfigs: {
        ...prev.communicationConfigs,
        azureIoT: {
          ...prev.communicationConfigs.azureIoT,
          settings: {
            ...prev.communicationConfigs.azureIoT?.settings,
            activeDeviceId: deviceId
          }
        }
      }
    }));
  };

  const handleGenerateFunction = async (protocolName) => {
    if (!flowNodeTypes) return;

    const {
      inputNodeType, outputNodeType,
      inputNodeName, outputNodeName,
      gatewayInProtocol, gatewayOutProtocol,
    } = flowNodeTypes;

    setGenerateError(null);

    try {
      const inputNodeConfig =
        component?.communicationConfigs?.modbus?.settings ??
        component?.communicationConfigs?.opcua?.settings  ??
        {};

      const outputNodeConfig =
        component?.communicationConfigs?.mqtt?.settings ??
        component?.communicationConfigs?.http?.settings ??
        {};

      console.log('🔍 circuit:', circuit);
      console.log('🔍 circuit connections:', circuit?.connections);

      // ✅ Build sensorMappings from circuit connections and simulator attachedSensors
      // Use parent circuit which has the actual sensor-to-PLC connections
      const parentCircuit = circuit?._parentCircuit || circuit;

      // In handleGenerateFunction, after building parentCircuit:
      const sensorMappings = await Promise.all(
        (parentCircuit?.connections || [])
          .filter(conn => conn.to.pinName?.startsWith('AI'))
          .map(async conn => {
            const registerIndex = parseInt(conn.to.pinName.replace('AI', ''));
            
            const sensorComp = parentCircuit.components?.find(
              c => c.instanceId === conn.from.componentInstanceId
            );
            if (!sensorComp) return null;

            const type = (sensorComp.componentId || '').toUpperCase();
            const scalingFactor =
              type.includes('DHT') || type.includes('TEMP') || type.includes('LM35') ? 10
              : type.includes('BMP') || type.includes('PRESSURE') ? 100
              : 1;

            // Use already-enriched data or fetch from library
            let simulationOutputs = sensorComp.componentLibraryData?.simulation?.outputs;
            if (!simulationOutputs) {
              try {
                const libResponse = await getComponentById(sensorComp.componentId);
                simulationOutputs = libResponse?.data?.simulation?.outputs || [];
              } catch {
                simulationOutputs = [];
              }
            }

            const fields = simulationOutputs.length > 0
              ? simulationOutputs.map(output => ({
                  name: output.name,
                  scalingFactor,
                  unit: output.unit || ''
                }))
              : [{ 
                  name: sensorComp.label || sensorComp.componentName,
                  scalingFactor,
                  unit: ''
                }];

            return {
              registerIndex,
              sensorName: sensorComp.label || sensorComp.componentName,
              sensorType: sensorComp.componentId,
              fields,
            };
          })
      );
      const filteredMappings = sensorMappings.filter(Boolean);
      console.log('🔍 sensorMappings:', JSON.stringify(sensorMappings, null, 2));

      console.log('🔍 params being sent:', JSON.stringify({
        inputNodeType,
        outputNodeType,
        sensorMappings: filteredMappings,
      }, null, 2));

      const response = await generateFunction({
        inputNodeType,
        outputNodeType,
        inputNodeName:      inputNodeName      ?? null,
        outputNodeName:     outputNodeName     ?? null,
        gatewayLabel:       gatewayLabel       ?? null,
        gatewayInProtocol:  gatewayInProtocol  ?? null,
        gatewayOutProtocol: gatewayOutProtocol ?? null,
        inputNodeConfig,
        outputNodeConfig,
        filteredMappings,
      });

      if (!response?.success) {
        throw new Error(response?.error ?? response?.message ?? 'Generation failed');
      }

      handleProtocolSettingChange(protocolName, 'func', response.data.code);

    } catch (err) {
      setGenerateError(err.message ?? 'Failed to generate function');
    }
  };
  
  // Legacy instances (synced before the Arguments Schema editor existed) may
  // still store argsSchema as a hand-typed JS-object-literal string, e.g.
  // "[{name:'query',type:'str'}]" — not valid JSON (unquoted keys, single
  // quotes). Coerce those into real arrays so the row editor always renders;
  // once the user edits and saves, it's persisted as a proper array from
  // then on.
  const coerceArgsSchema = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (typeof raw !== 'string' || !raw.trim()) return [];
    try {
      const jsonish = raw
        .replace(/'/g, '"')
        .replace(/([{,]\s*)([a-zA-Z_][a-zA-Z0-9_]*)\s*:/g, '$1"$2":');
      const parsed = JSON.parse(jsonish);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  // Legacy AG_GRAPH_STATE instances store `fields` as a plain string array
  // (["messages", "next", ...]) or a stringified bracket-list. Coerce both
  // into the new object-row shape [{name, description, initialValue}] so
  // the row editor always renders; saving persists the new shape from then on.
  // const coerceStateFields = (raw) => {
  //   if (Array.isArray(raw)) {
  //     return raw.map(f =>
  //       typeof f === 'string'
  //         ? { name: f, description: '', initialValue: '' }
  //         : { name: f?.name ?? '', description: f?.description ?? '', initialValue: f?.initialValue ?? '' }
  //     );
  //   }
  //   if (typeof raw === 'string' && raw.trim()) {
  //     const listMatch = raw.trim().match(/^\[([\s\S]*)\]$/);
  //     if (listMatch) {
  //       return listMatch[1]
  //         .split(',')
  //         .map(s => s.trim().replace(/^['"]|['"]$/g, ''))
  //         .filter(Boolean)
  //         .map(name => ({ name, description: '', initialValue: '' }));
  //     }
  //   }
  //   return [];
  // };

  const coerceStateFields = (raw) => {
    if (Array.isArray(raw)) {
      return raw.map(f =>
        typeof f === 'string'
          ? { name: f, description: '', initialValue: '', type: 'string' }
          : {
              name: f?.name ?? '',
              description: f?.description ?? '',
              initialValue: f?.initialValue ?? '',
              type: f?.type ?? 'string',
            }
      );
    }
    if (typeof raw === 'string' && raw.trim()) {
      const listMatch = raw.trim().match(/^\[([\s\S]*)\]$/);
      if (listMatch) {
        return listMatch[1]
          .split(',')
          .map(s => s.trim().replace(/^['"]|['"]$/g, ''))
          .filter(Boolean)
          .map(name => ({ name, description: '', initialValue: '', type: 'string' }));
      }
    }
    return [];
  };

  const coerceGenericToolDataSource = (settings) => {
    if (!settings || settings.dataSource === 'chain' || settings.dataSource === 'state' || settings.dataSource === 'none') {
      return settings; // already v2, or not this component
    }
    if (settings.dataSource === 'retriever') {
      return { ...settings, dataSource: 'chain', invokeMode: 'single_string' };
    }
    if (settings.dataSource === 'llm') {
      return { ...settings, dataSource: 'chain', invokeMode: 'messages' };
    }
    return settings;
  };

  // ✅ NEW: Render Azure IoT Hub specific settings
  const renderProtocolSettings = (protocolName, protocolConfig) => {
    const settings = protocolConfig.settings || {};
    const validationRules = protocolConfig.validationRules || {};
    const isEnabled = config.communicationConfigs[protocolName]?.enabled || false;

    if (!isEnabled) return null;

    if (protocolName === 'azureIoT') {
        return renderAzureIoTHubSettings();
    }

    // Live (in-progress, unsaved) settings for this protocol — used to read
    // the current value of whatever field controls conditional visibility
    // (e.g. parserType, runnableMode), so hiding/showing reacts immediately
    // as the student changes the dropdown, before Save.
    const liveProtocolSettings = config.communicationConfigs[protocolName]?.settings || settings;

    // ← FIXED: union of settings keys AND validationRules keys, so a field
    // with an empty/default value (e.g. routeMap: {}) still renders, as long
    // as it's declared in the schema.
    const allKeys = Array.from(new Set([
        ...Object.keys(settings),
        ...Object.keys(validationRules),
    ]));

    return (
        <div className="config-settings-form">
            {allKeys
                .filter(settingKey => isFieldVisible(component.componentId, settingKey, liveProtocolSettings))
                .map(settingKey => {
                // ← FIXED: fall back to a type-appropriate empty value instead of
                // just undefined, when the key isn't present in `settings` at all.
                const rules = validationRules[settingKey];
                const defaultEmpty = rules?.type === 'object' ? {} : rules?.type === 'array' ? [] : '';
                const value = config.communicationConfigs[protocolName]?.settings?.[settingKey]
                    ?? settings[settingKey]
                    ?? defaultEmpty;
                const settingType = typeof settings[settingKey];
                const enumOptions = rules?.enum;
                const isReadonly = rules?.readonly === true;

                if (settingKey === 'func') {
                  return (
                    <div key={settingKey} className="config-form-group">

                      {/* Header row: label + Generate button */}
                      <div className="config-func-header">
                        <label className="config-label">Function Code (JavaScript):</label>
                        {flowNodeTypes && (
                          <button
                            className={`config-generate-btn ${isGenerating ? 'loading' : ''}`}
                            onClick={() => handleGenerateFunction(protocolName)}
                            disabled={isGenerating}
                            title={`Generate function for ${flowNodeTypes.inputNodeType} → ${flowNodeTypes.outputNodeType}`}
                          >
                            {isGenerating ? '⏳ Generating...' : '✨ Generate Function'}
                          </button>
                        )}
                      </div>

                      {/* Context hint when flowNodeTypes available */}
                      {flowNodeTypes && (
                        <div className="config-func-context">
                          <span className="config-func-context-pill">
                            📥 {flowNodeTypes.inputNodeName ?? flowNodeTypes.inputNodeType ?? '—'}
                          </span>
                          <span className="config-func-context-arrow">→</span>
                          <span className="config-func-context-pill">
                            ⚙️ Function
                          </span>
                          <span className="config-func-context-arrow">→</span>
                          <span className="config-func-context-pill">
                            📤 {flowNodeTypes.outputNodeName ?? flowNodeTypes.outputNodeType ?? '—'}
                          </span>
                        </div>
                      )}

                      {/* Error message */}
                      {generateError && (
                        <div className="config-generate-error">
                          ❌ {generateError}
                        </div>
                      )}

                      {/* Monaco Editor */}
                      <div className="config-monaco-wrapper">
                        <Editor
                          height="320px"
                          defaultLanguage="javascript"
                          value={value || '// msg.payload is the input\n// Return msg to pass it on\nreturn msg;'}
                          onChange={(val) =>
                            handleProtocolSettingChange(protocolName, settingKey, val ?? '')
                          }
                          theme="vs-dark"
                          options={{
                            minimap:        { enabled: false },
                            fontSize:       13,
                            lineNumbers:    'on',
                            scrollBeyondLastLine: false,
                            wordWrap:       'on',
                            tabSize:        2,
                            automaticLayout: true,
                            padding:        { top: 8, bottom: 8 },
                          }}
                        />
                      </div>

                      <small className="config-hint">
                        Full Node.js available. Return <code>null</code> to stop the message.
                      </small>
                    </div>
                  );
                }

                if (settingKey === 'outputs') {
                    return (
                        <div key={settingKey} className="config-form-group">
                            <label className="config-label">Number of Outputs:</label>
                            <input
                                type="number"
                                className="config-input"
                                value={value}
                                min={1}
                                max={10}
                                onChange={(e) =>
                                    handleProtocolSettingChange(protocolName, settingKey, parseInt(e.target.value))
                                }
                            />
                            <small className="config-hint">How many output ports this Function node has (1–10)</small>
                        </div>
                    );
                }

                if (settingKey === 'initialize' || settingKey === 'finalize') {
                    return (
                        <div key={settingKey} className="config-form-group">
                            <label className="config-label">
                                {settingKey === 'initialize' ? 'Initialize (runs on deploy):' : 'Finalize (runs on stop):'}
                            </label>
                            <textarea
                                className="config-input"
                                rows={4}
                                value={value}
                                onChange={(e) =>
                                    handleProtocolSettingChange(protocolName, settingKey, e.target.value)
                                }
                                placeholder="// Optional lifecycle code"
                                style={{ fontFamily: 'Courier New, monospace', fontSize: '12px', resize: 'vertical' }}
                            />
                        </div>
                    );
                }

                if (settingKey === 'inputs' && Array.isArray(settings[settingKey])) {
                  const items = value || [];

                  const updateItem = (index, field, newVal) => {
                    const updated = items.map((item, i) => i === index ? { ...item, [field]: newVal } : item);
                    handleProtocolSettingChange(protocolName, settingKey, updated);
                  };

                  const autoResize = (e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  };

                  const addItem = () => {
                    handleProtocolSettingChange(protocolName, settingKey, [
                      ...items,
                      { system_msg: '', human_msg: '' }
                    ]);
                  };

                  const removeItem = (index) => {
                    handleProtocolSettingChange(protocolName, settingKey, items.filter((_, i) => i !== index));
                  };

                  return (
                    <div key={settingKey} style={{ display: 'block', width: '100%' }}>
                      <label className="config-label" style={{ display: 'block', marginBottom: '10px' }}>
                        Prompt Inputs ({items.length}):
                      </label>

                      {items.map((item, index) => (
                        <div key={index} style={{
                          display: 'block',
                          width: '100%',
                          padding: '14px',
                          marginBottom: '12px',
                          border: '1px solid #e2e8f0',
                          borderRadius: '6px',
                          backgroundColor: '#f8f9fa',
                          boxSizing: 'border-box',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <strong style={{ fontSize: '0.9em' }}>Input {index + 1}</strong>
                            <button type="button" onClick={() => removeItem(index)} style={{
                              background: '#dc3545', color: 'white', border: 'none',
                              borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '0.8em'
                            }}>
                              ✖ Remove
                            </button>
                          </div>

                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                          }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '0.8em', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                              System message
                            </label>
                            <textarea
                              rows={3}
                              value={item.system_msg}
                              onChange={(e) => {
                                updateItem(index, 'system_msg', e.target.value);
                                resizeTextarea(e.target);
                              }}
                              placeholder="e.g. You are a helpful assistant."
                              className="config-textarea-autosize"
                            />
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label style={{ fontSize: '0.8em', fontWeight: 600, marginBottom: '4px', color: '#475569' }}>
                              Human message
                            </label>
                            <textarea
                              rows={3}
                              value={item.human_msg}
                              onChange={(e) => {
                                updateItem(index, 'human_msg', e.target.value);
                                resizeTextarea(e.target);
                              }}
                              placeholder="e.g. Tell me a fact about..."
                              className="config-textarea-autosize"
                            />
                          </div>
                          </div>
                        </div>
                      ))}

                      <button type="button" onClick={addItem} style={{
                        width: '100%', padding: '10px', background: '#28a745', color: 'white',
                        border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold'
                      }}>
                        ➕ Add Input
                      </button>
                    </div>
                  );
                }

                if (settingKey === 'field' && component.category === 'nr_dashboard') {
                  const wiredSource = getWiredSourceForPin('NR_IN');
                  const suggestedFields = wiredSource?.suggestedFields ?? [];

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Field (Payload Key):</label>

                      {suggestedFields.length > 0 ? (
                        <>
                          <select
                            className="config-input"
                            value={value}
                            onChange={(e) =>
                              handleProtocolSettingChange(protocolName, settingKey, e.target.value)
                            }
                          >
                            <option value="">— select a field —</option>
                            {suggestedFields.map(f => (
                              <option key={f} value={f}>{f}</option>
                            ))}
                          </select>
                          <small className="config-hint">
                            Detected from connected {wiredSource.label} ({wiredSource.nodeRedType}).
                            Select the value this widget should display.
                          </small>
                        </>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="config-input"
                            value={value}
                            onChange={(e) =>
                              handleProtocolSettingChange(protocolName, settingKey, e.target.value)
                            }
                            placeholder="e.g. payload.temperature or payload[0]"
                          />
                          <small className="config-hint">
                            {wiredSource
                              ? `Connected from ${wiredSource.label} — no payload fields detected. Type the field path manually.`
                              : 'Wire this node to a Function or Modbus Read node first, then the field list will appear automatically.'}
                          </small>
                        </>
                      )}
                    </div>
                  );
                }

                if (settingKey === 'pydanticFields' && Array.isArray(settings[settingKey])) {
                  const fields = value || [];
                  const updateField = (i, key, v) => {
                    const updated = fields.map((f, idx) => idx === i ? { ...f, [key]: v } : f);
                    handleProtocolSettingChange(protocolName, settingKey, updated);
                  };
                  const addField = () => handleProtocolSettingChange(protocolName, settingKey, [...fields, { name: '', type: 'str', description: '' }]);
                  const removeField = (i) => handleProtocolSettingChange(protocolName, settingKey, fields.filter((_, idx) => idx !== i));

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Pydantic Fields ({fields.length}):</label>
                      {fields.map((f, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input className="config-input" placeholder="name" value={f.name}
                                onChange={e => updateField(i, 'name', e.target.value)} />
                          <select className="config-input" value={f.type}
                                  onChange={e => updateField(i, 'type', e.target.value)}>
                            {['str','int','float','bool','List[str]'].map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <input className="config-input" placeholder="description" value={f.description}
                                onChange={e => updateField(i, 'description', e.target.value)} />
                          <button onClick={() => removeField(i)}>✖</button>
                        </div>
                      ))}
                      <button onClick={addField}>➕ Add Field</button>
                    </div>
                  );
                }

                if (settingKey === 'argsSchema') {
                  const args = coerceArgsSchema(value);
                  const updateArg = (i, key, v) => {
                    const updated = args.map((a, idx) => idx === i ? { ...a, [key]: v } : a);
                    handleProtocolSettingChange(protocolName, settingKey, updated);
                  };
                  const addArg = () => handleProtocolSettingChange(protocolName, settingKey, [
                    ...args, { name: '', type: 'str', description: '', required: true, default: null }
                  ]);
                  const removeArg = (i) => handleProtocolSettingChange(protocolName, settingKey, args.filter((_, idx) => idx !== i));

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Arguments Schema ({args.length}):</label>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1.2fr 0.8fr 1.3fr 110px 1fr auto',
                        gap: '8px',
                        marginBottom: '2px',
                        fontSize: '0.78em',
                        fontWeight: 600,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                      }}>
                        <span>Name</span>
                        <span>Type</span>
                        <span>Description</span>
                        <span style={{ whiteSpace: 'normal', lineHeight: 1.2, display: 'block' }}>LLM Required</span>
                        <span>Default</span>
                        <button style={{ visibility: 'hidden' }}>✖</button>
                      </div>
                      {args.map((a, i) => (
                        <div key={i} style={{
                          display: 'grid',
                          gridTemplateColumns: '1.2fr 0.8fr 1.3fr 110px 1fr auto',
                          gap: '8px',
                          alignItems: 'center',
                          marginBottom: '4px',
                        }}>
                          <input className="config-input" placeholder="name" value={a.name}
                                onChange={e => updateArg(i, 'name', e.target.value)} />
                          <select className="config-input" value={a.type}
                                  onChange={e => updateArg(i, 'type', e.target.value)}>
                            {['str', 'int', 'float', 'bool'].map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                          <input className="config-input" placeholder="description" value={a.description || ''}
                                onChange={e => updateArg(i, 'description', e.target.value)} />
                          <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', margin: 0, gap: '4px', fontSize: '0.8em' }}>
                            <input type="checkbox" checked={a.required ?? true}
                                  onChange={e => updateArg(i, 'required', e.target.checked)}
                                  style={{ margin: 0 }} />
                            {/* LLM required */}
                          </label>
                              <input className="config-input" placeholder="default" disabled={a.required ?? true}
                                type={a.type === 'int' || a.type === 'float' ? 'number' : a.type === 'bool' ? 'text' : 'text'}
                                value={a.required ? '' : (a.default ?? '')}
                                onChange={e => {
                                  const raw = e.target.value;
                                  let coerced = raw;
                                  if (a.type === 'int')   coerced = raw === '' ? null : parseInt(raw, 10);
                                  if (a.type === 'float') coerced = raw === '' ? null : parseFloat(raw);
                                  if (a.type === 'bool')  coerced = raw === 'true';
                                  updateArg(i, 'default', coerced);
                                }}
                                style={a.required ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}} />
                          <button onClick={() => removeArg(i)}>✖</button>
                        </div>
                      ))}
                      <button onClick={addArg}>➕ Add Arg</button>
                      <small className="config-hint">
                        primaryArg must reference one of these names — used when invokeMode='single_string' or dataSource='state'.
                      </small>
                    </div>
                  );
                }

                if (settingKey === 'fields' && component.componentId === 'AG_GRAPH_STATE') {
                  const rows = coerceStateFields(value);
                  const updateRow = (i, key, v) => {
                    const updated = rows.map((r, idx) => idx === i ? { ...r, [key]: v } : r);
                    handleProtocolSettingChange(protocolName, settingKey, updated);
                  };
                  const addRow = () => handleProtocolSettingChange(protocolName, settingKey, [
                    ...rows, { name: '', description: '', initialValue: '', type: 'string' }
                  ]);
                  const removeRow = (i) => handleProtocolSettingChange(protocolName, settingKey, rows.filter((_, idx) => idx !== i));

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">State Fields ({rows.length}):</label>
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 2fr 0.8fr 1fr auto',   // ← was '1fr 2fr 1fr auto'
                        gap: '8px',
                        marginBottom: '6px',
                        fontSize: '0.78em',
                        fontWeight: 600,
                        color: '#64748b',
                        textTransform: 'uppercase',
                        letterSpacing: '0.02em',
                      }}>
                        <span>Name</span>
                        <span>Description</span>
                        <span>Type</span>
                        <span>Initial Value</span>
                        <span></span>
                      </div>
                      {rows.map((r, i) => {
                        const isMessages = r.name === 'messages';
                        return (
                          <div key={i} style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 2fr 0.8fr 1fr auto',  // ← was '1fr 2fr 1fr auto'
                            gap: '8px',
                            alignItems: 'center',
                            marginBottom: '8px',
                          }}>
                            <input className="config-input" placeholder="e.g. guard_score" value={r.name}
                                  onChange={e => updateRow(i, 'name', e.target.value)} />
                            <input className="config-input" placeholder="what this field holds" value={r.description}
                                  onChange={e => updateRow(i, 'description', e.target.value)} />
                            <select
                              className="config-input"
                              value={r.type || 'string'}
                              disabled={isMessages}
                              onChange={e => updateRow(i, 'type', e.target.value)}
                              style={isMessages ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                            >
                              <option value="string">string</option>
                              <option value="number">number</option>
                              <option value="boolean">boolean</option>
                              <option value="list">list</option>
                            </select>
                            <input
                              className="config-input"
                              placeholder={isMessages ? '[] (fixed — reducer field)' : 'e.g. 0, [], null'}
                              value={isMessages ? '' : r.initialValue}
                              disabled={isMessages}
                              style={isMessages ? { backgroundColor: '#f3f4f6', cursor: 'not-allowed' } : {}}
                              onChange={e => updateRow(i, 'initialValue', e.target.value)}
                            />
                            <button onClick={() => removeRow(i)}>✖</button>
                          </div>
                        );
                      })}
                      <button onClick={addRow}>➕ Add Field</button>
                      <small className="config-hint">
                        Leave Initial Value blank to default to <code>null</code> (or <code>[]</code> for the messages field).
                        Values are parsed as JSON if valid (e.g. <code>0</code>, <code>[]</code>, <code>"idle"</code>), otherwise kept as plain text.
                      </small>
                    </div>
                  );
                }

                if (settingKey === 'primaryArg') {
                  const rawArgsSchema =
                    config.communicationConfigs[protocolName]?.settings?.argsSchema
                    ?? settings.argsSchema
                    ?? [];
                  const liveArgsSchema = coerceArgsSchema(rawArgsSchema);
                  const argNames = liveArgsSchema.map(a => a?.name).filter(Boolean);

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Primary Arg:</label>
                      {argNames.length > 0 ? (
                        <select
                          className="config-input"
                          value={argNames.includes(value) ? value : ''}
                          onChange={(e) => handleProtocolSettingChange(protocolName, settingKey, e.target.value)}
                        >
                          <option value="" disabled>— select an arg —</option>
                          {argNames.map(name => <option key={name} value={name}>{name}</option>)}
                        </select>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="config-input"
                            value={value}
                            disabled
                            style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                          />
                          <small className="config-hint" style={{ color: '#ef4444' }}>
                            Add at least one entry to Args Schema first.
                          </small>
                        </>
                      )}
                      {argNames.length > 0 && !argNames.includes(value) && (
                        <small className="config-hint" style={{ color: '#ef4444' }}>
                          Current value "{value}" isn't in Args Schema — select a valid one.
                        </small>
                      )}
                    </div>
                  );
                }

                if (settingKey === 'sourceNode' && component.componentId === 'AG_GRAPH_COND_EDGE') {
                  const sourceWire = circuit?.connections?.find(c =>
                    c.to?.componentInstanceId === component.instanceId && c.to?.pinName === 'SOURCE_IN'
                  );
                  const sourceComp = sourceWire
                    ? circuit?.components?.find(c => c.instanceId === sourceWire.from.componentInstanceId)
                    : null;
                  const derivedSourceNode = sourceComp
                    ? (sourceComp.communicationConfigs?.simpleNodeConfig?.settings?.nodeName
                        || sourceComp.communicationConfigs?.agentNodeConfig?.settings?.nodeName
                        || sourceComp.communicationConfigs?.subgraphConfig?.settings?.nodeName
                        || sourceComp.label
                        || sourceComp.componentName)
                    : null;

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Source Node:</label>
                      <input
                        type="text"
                        className="config-input"
                        value={derivedSourceNode || ''}
                        readOnly
                        disabled
                        placeholder="— not wired —"
                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                      <small className="config-hint">
                        Auto-derived from the wire into SOURCE_IN — wire a node's FLOW_OUT to this edge's SOURCE_IN.
                      </small>
                    </div>
                  );
                }

                if (settingKey === 'branches' && component.componentId === 'AG_GRAPH_COND_EDGE') {
                  const branchWires = circuit?.connections?.filter(c =>
                    c.from?.componentInstanceId === component.instanceId && c.from?.pinName === 'BRANCH_OUT'
                  ) || [];

                  const derivedBranches = branchWires.map(w => {
                    const targetComp = circuit?.components?.find(c => c.instanceId === w.to.componentInstanceId);
                    // const targetName = targetComp
                    //   ? (targetComp.isEndState
                    //       ? 'END'
                    //       : (targetComp.communicationConfigs?.simpleNodeConfig?.settings?.nodeName
                        const targetName = targetComp
                          ? (targetComp.componentId === 'AG_GRAPH_END_NODE'
                              ? 'END'
                              : (targetComp.communicationConfigs?.simpleNodeConfig?.settings?.nodeName
                              || targetComp.communicationConfigs?.agentNodeConfig?.settings?.nodeName
                              || targetComp.communicationConfigs?.subgraphConfig?.settings?.nodeName
                              || targetComp.label
                              || targetComp.componentName))
                      : null;
                    return { label: w.label, target: targetName };
                  }).filter(b => b.label && b.target);

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Branches ({derivedBranches.length}):</label>
                      {derivedBranches.length > 0 ? (
                        <div style={{
                          padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: '6px',
                          backgroundColor: '#f8f9fa', fontSize: '0.85em',
                        }}>
                          {derivedBranches.map(({ label, target }) => (
                            <div key={label} style={{ display: 'flex', gap: '8px', marginBottom: '4px' }}>
                              <code>{label}</code>
                              <span>→</span>
                              <code>{target}</code>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <small className="config-hint">
                          No branches yet — wire BRANCH_OUT to target nodes and set a label on each wire in Wire Properties. The label becomes the branch key.
                        </small>
                      )}
                    </div>
                  );
                }

                if ((settingKey === 'sourceNode' || settingKey === 'targetNode') && component.componentId === 'AG_GRAPH_SEND_DISPATCH') {
                  const pinName = settingKey === 'sourceNode' ? 'FLOW_IN' : 'TARGET_OUT';
                  const wire = settingKey === 'sourceNode'
                    ? circuit?.connections?.find(c => c.to?.componentInstanceId === component.instanceId && c.to?.pinName === pinName)
                    : circuit?.connections?.find(c => c.from?.componentInstanceId === component.instanceId && c.from?.pinName === pinName);
                  const neighborInstanceId = settingKey === 'sourceNode' ? wire?.from?.componentInstanceId : wire?.to?.componentInstanceId;
                  const neighborComp = neighborInstanceId
                    ? circuit?.components?.find(c => c.instanceId === neighborInstanceId)
                    : null;
                  const derivedValue = neighborComp
                    ? (neighborComp.communicationConfigs?.simpleNodeConfig?.settings?.nodeName
                        || neighborComp.communicationConfigs?.agentNodeConfig?.settings?.nodeName
                        || neighborComp.communicationConfigs?.subgraphConfig?.settings?.nodeName
                        || neighborComp.label
                        || neighborComp.componentName)
                    : null;
                  const label = settingKey === 'sourceNode' ? 'Source Node' : 'Target Node';
                  const hint = settingKey === 'sourceNode'
                    ? 'Auto-derived from the wire into FLOW_IN — wire the upstream node\'s FLOW_OUT here.'
                    : 'Auto-derived from the wire out of TARGET_OUT — wire this here into the worker node\'s FLOW_IN.';

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">{label}:</label>
                      <input
                        type="text"
                        className="config-input"
                        value={derivedValue || ''}
                        readOnly
                        disabled
                        placeholder="— not wired —"
                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                      <small className="config-hint">{hint}</small>
                    </div>
                  );
                }

                if (settingKey === 'routerLogic' && component.componentId === 'AG_GRAPH_COND_EDGE') {
                  return (
                    <div key={settingKey} className="config-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label className="config-label" style={{ marginBottom: 0 }}>Router Logic:</label>
                        <button
                          type="button"
                          className="config-generate-btn"
                          onClick={() => {
                            setLogicBuilderProtocol(protocolName);
                            setShowLogicBuilder(true);
                          }}
                        >
                          🧩 Logic Builder
                        </button>
                      </div>
                      <input
                        type="text"
                        className="config-input"
                        value={value}
                        onChange={(e) => handleProtocolSettingChange(protocolName, settingKey, e.target.value)}
                        placeholder="e.g. 'pass' if state.get('guard_score', 0) >= 0.7 else 'fail'"
                        style={{ fontFamily: 'Courier New, monospace', fontSize: '12.5px' }}
                      />
                      <small className="config-hint">
                        Use the Logic Builder for a guided setup, or edit this Python expression directly.
                      </small>
                    </div>
                  );
                }

                if (settingKey === 'returnUpdates' && component.componentId === 'AG_GRAPH_SIMPLE_NODE') {
                  const entries = Object.entries(value || {});
                  const stringifyForEdit = (v) => (typeof v === 'string' ? v : JSON.stringify(v, null, 2));
                  const parseFromEdit = (text) => {
                    try {
                      return JSON.parse(text);
                    } catch {
                      return text;
                    }
                  };
                  const updateKey = (i, newKey) => {
                    const arr = entries.slice();
                    arr[i] = [newKey, arr[i][1]];
                    handleProtocolSettingChange(protocolName, settingKey, Object.fromEntries(arr));
                  };
                  const updateValue = (i, text) => {
                    const arr = entries.slice();
                    arr[i] = [arr[i][0], parseFromEdit(text)];
                    handleProtocolSettingChange(protocolName, settingKey, Object.fromEntries(arr));
                  };
                  const addEntry = () => {
                    handleProtocolSettingChange(protocolName, settingKey, { ...(value || {}), '': '' });
                  };
                  const removeEntry = (i) => {
                    const arr = entries.filter((_, idx) => idx !== i);
                    handleProtocolSettingChange(protocolName, settingKey, Object.fromEntries(arr));
                  };

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Return Updates ({entries.length}):</label>
                      {entries.map(([k, v], i) => (
                        <div key={i} style={{
                          display: 'block', width: '100%', padding: '10px', marginBottom: '8px',
                          border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f8f9fa', boxSizing: 'border-box',
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', gap: '8px' }}>
                            <input
                              className="config-input"
                              placeholder="state key, e.g. subtasks"
                              value={k}
                              style={{ fontWeight: 600 }}
                              onChange={(e) => updateKey(i, e.target.value)}
                            />
                            <button type="button" onClick={() => removeEntry(i)} style={{
                              background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px',
                              padding: '4px 10px', cursor: 'pointer', fontSize: '0.8em', flexShrink: 0
                            }}>
                              ✖ Remove
                            </button>
                          </div>
                          <textarea
                            className="config-input"
                            rows={Math.min(20, Math.max(typeof v === 'string' ? 1 : 4, stringifyForEdit(v).split('\n').length))}
                            value={stringifyForEdit(v)}
                            placeholder='Plain text, or valid JSON for a structured value, e.g. [{"student_answer": "...", "expected_answer": "..."}]'
                            style={{ fontFamily: 'Courier New, monospace', fontSize: '12.5px', width: '100%', boxSizing: 'border-box', resize: 'vertical' }}
                            onChange={(e) => updateValue(i, e.target.value)}
                          />
                        </div>
                      ))}
                      <button type="button" onClick={addEntry} style={{ width: '100%' }}>➕ Add</button>
                      <small className="config-hint">
                        Plain text becomes a string. Valid JSON (a list, object, number, or boolean) becomes that structured value — e.g. key "subtasks" with a JSON list of dicts, for Send-dispatch fan-out.
                      </small>
                    </div>
                  );
                }

                if (settingKey === 'accumulatingFields' && component.componentId === 'AG_GRAPH_STATE') {
                  // accumulatingFields only ever does anything when it names a
                  // field already declared in this SAME component's own
                  // "fields" setting — a free-text box lets a typo silently do
                  // nothing. Multi-select checkboxes instead, built from the
                  // live (in-progress) fields list, excluding messages/
                  // execution_trace which already accumulate unconditionally.
                  const rawFields = liveProtocolSettings.fields;
                  const candidateNames = (Array.isArray(rawFields)
                    ? rawFields.map(f => (typeof f === 'string' ? f : f?.name)).filter(Boolean)
                    : []
                  ).filter(n => n !== 'messages' && n !== 'execution_trace');
                  const selected = Array.isArray(value) ? value : [];
                  const toggle = (name) => {
                    const next = selected.includes(name)
                      ? selected.filter(n => n !== name)
                      : [...selected, name];
                    handleProtocolSettingChange(protocolName, settingKey, next);
                  };

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Accumulating Fields ({selected.length}):</label>
                      {candidateNames.length > 0 ? (
                        candidateNames.map(name => (
                          <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <input
                              type="checkbox"
                              checked={selected.includes(name)}
                              onChange={() => toggle(name)}
                            />
                            <span>{name}</span>
                          </label>
                        ))
                      ) : (
                        <small className="config-hint" style={{ color: '#ef4444' }}>
                          No fields defined yet (other than messages) — add one in "fields" above first.
                        </small>
                      )}
                      {rules?.note && <small className="config-hint">{rules.note}</small>}
                    </div>
                  );
                }

                if (settingKey === 'interruptBefore' && component.componentId === 'AG_GRAPH_BUILDER') {
                  // interruptBefore only works when it names a real node in
                  // this builder's own sub-canvas — a free-text box lets a
                  // typo (e.g. 'tutor' instead of 'tutor_node') pass UI
                  // validation and only fail at graph.compile() time.
                  // Multi-select checkboxes instead, built from the live
                  // sub-canvas node list, same pattern as accumulatingFields.
                  const REAL_GRAPH_NODE_IDS = ['AG_GRAPH_SIMPLE_NODE', 'AG_GRAPH_AGENT_NODE', 'AG_GRAPH_SUBGRAPH'];
                  const subNodes = component.graphFlow?.nodes || [];
                  const getNodeName = (n) =>
                    n.communicationConfigs?.simpleNodeConfig?.settings?.nodeName
                    || n.communicationConfigs?.agentNodeConfig?.settings?.nodeName
                    || n.communicationConfigs?.subgraphConfig?.settings?.nodeName
                    || n.label;
                  const candidateNames = subNodes
                    .filter(n => REAL_GRAPH_NODE_IDS.includes(n.componentId))
                    .map(getNodeName)
                    .filter(Boolean);
                  const selected = Array.isArray(value) ? value : [];
                  const toggle = (name) => {
                    const next = selected.includes(name)
                      ? selected.filter(n => n !== name)
                      : [...selected, name];
                    handleProtocolSettingChange(protocolName, settingKey, next);
                  };

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Interrupt Before ({selected.length}):</label>
                      {candidateNames.length > 0 ? (
                        candidateNames.map(name => (
                          <label key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                            <input
                              type="checkbox"
                              checked={selected.includes(name)}
                              onChange={() => toggle(name)}
                            />
                            <span>{name}</span>
                          </label>
                        ))
                      ) : (
                        <small className="config-hint" style={{ color: '#ef4444' }}>
                          No graph nodes defined yet in the sub-canvas — add one first.
                        </small>
                      )}
                      {rules?.note && <small className="config-hint">{rules.note}</small>}
                    </div>
                  );
                }

                if (rules?.type === 'array' && settingKey !== 'argsSchema') {
                  // Generic editor for a plain array-of-strings setting (e.g.
                  // accumulatingFields) — no dedicated UI existed for these
                  // before, so typing into a bare text input just saved a
                  // literal string instead of a real array.
                  const items = Array.isArray(value) ? value : [];
                  const updateItem = (i, newVal) => {
                    const arr = items.slice();
                    arr[i] = newVal;
                    handleProtocolSettingChange(protocolName, settingKey, arr);
                  };
                  const addItem = () => {
                    handleProtocolSettingChange(protocolName, settingKey, [...items, '']);
                  };
                  const removeItem = (i) => {
                    handleProtocolSettingChange(protocolName, settingKey, items.filter((_, idx) => idx !== i));
                  };
                  const label = settingKey.replace(/([A-Z])/g, ' $1').trim();

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">
                        {label.charAt(0).toUpperCase() + label.slice(1)} ({items.length}):
                      </label>
                      {items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                          <input
                            className="config-input"
                            placeholder="value"
                            value={item}
                            onChange={(e) => updateItem(i, e.target.value)}
                          />
                          <button type="button" onClick={() => removeItem(i)}>✖</button>
                        </div>
                      ))}
                      <button type="button" onClick={addItem}>➕ Add</button>
                      {rules?.note && <small className="config-hint">{rules.note}</small>}
                    </div>
                  );
                }

                if (rules?.type === 'object') {
                  const entries = Object.entries(value || {});
                  const updateEntry = (i, part, newVal) => {
                    const arr = entries.slice();
                    arr[i] = part === 'key' ? [newVal, arr[i][1]] : [arr[i][0], newVal];
                    handleProtocolSettingChange(protocolName, settingKey, Object.fromEntries(arr));
                  };
                  const addEntry = () => {
                    handleProtocolSettingChange(protocolName, settingKey, { ...(value || {}), '': '' });
                  };
                  const removeEntry = (i) => {
                    const arr = entries.filter((_, idx) => idx !== i);
                    handleProtocolSettingChange(protocolName, settingKey, Object.fromEntries(arr));
                  };
                  const label = settingKey.replace(/([A-Z])/g, ' $1').trim();

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">
                        {label.charAt(0).toUpperCase() + label.slice(1)} ({entries.length}):
                      </label>
                      {entries.map(([k, v], i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                          <input className="config-input" placeholder="key" value={k}
                                onChange={e => updateEntry(i, 'key', e.target.value)} />
                          <span style={{ alignSelf: 'center' }}>→</span>
                          <input className="config-input" placeholder="value" value={v}
                                onChange={e => updateEntry(i, 'val', e.target.value)} />
                          <button onClick={() => removeEntry(i)}>✖</button>
                        </div>
                      ))}
                      <button onClick={addEntry}>➕ Add</button>
                      {rules?.note && <small className="config-hint">{rules.note}</small>}
                    </div>
                  );
                }

                if (settingKey === 'entryPoint' && component.componentId === 'AG_GRAPH_BUILDER') {
                  const subNodes = component.graphFlow?.nodes || [];
                  const initialNode = subNodes.find(
                    n => n.communicationConfigs?.agentNodeConfig?.settings?.isInitialNode === true ||
                         n.communicationConfigs?.simpleNodeConfig?.settings?.isInitialNode === true
                  );
                  const derivedEntryPoint = initialNode
                    ? (initialNode.communicationConfigs?.agentNodeConfig?.settings?.nodeName
                        || initialNode.communicationConfigs?.simpleNodeConfig?.settings?.nodeName
                        || initialNode.label
                        || initialNode.componentName)
                    : null;

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Entry Point:</label>
                      <input
                        type="text"
                        className="config-input"
                        value={derivedEntryPoint || ''}
                        readOnly
                        disabled
                        placeholder="— no node marked as initial —"
                        style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                      />
                      <small className="config-hint">
                        Auto-derived from the sub-canvas node with "Is Initial Node" checked.
                      </small>
                    </div>
                  );
                }

                if (settingKey === 'childBuilderInstanceId' && component.componentId === 'AG_GRAPH_SUBGRAPH') {
                  // AG_GRAPH_SUBGRAPH is sub-canvas-only, but the Builder it
                  // wraps only exists on the main canvas — there is no single
                  // canvas view where both endpoints are visible, so this
                  // can't be a drawn wire. Resolved implicitly instead, the
                  // same way STATE_IN/AG_GRAPH_STATE already is (see
                  // circuit._parentCircuit lookup a few blocks below).
                  const parentComponents = circuit?._parentCircuit?.components || [];
                  const currentBuilderId = circuit?._graphInstanceId;
                  const builderOptions = parentComponents.filter(c =>
                    c.componentId === 'AG_GRAPH_BUILDER' && c.instanceId !== currentBuilderId
                  );

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">Child Graph (Builder to wrap):</label>
                      {builderOptions.length > 0 ? (
                        <select
                          className="config-input"
                          value={builderOptions.some(b => b.instanceId === value) ? value : ''}
                          onChange={(e) => handleProtocolSettingChange(protocolName, settingKey, e.target.value)}
                        >
                          <option value="" disabled>— select a Builder —</option>
                          {builderOptions.map(b => (
                            <option key={b.instanceId} value={b.instanceId}>
                              {b.label || b.componentName} ({b.instanceId})
                            </option>
                          ))}
                        </select>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="config-input"
                            value=""
                            disabled
                            style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                          />
                          <small className="config-hint" style={{ color: '#ef4444' }}>
                            No other AG_GRAPH_BUILDER instance found on the main canvas yet — add one first.
                          </small>
                        </>
                      )}
                      <small className="config-hint">
                        Resolved implicitly (like Agent State) — no wire needed, since this component lives in a sub-canvas and GRAPH_OUT only exists on main-canvas Builders.
                      </small>
                    </div>
                  );
                }

                if ( (settingKey === 'contextField' || settingKey === 'outputField' || settingKey === 'groundingField') && component.componentId === 'AG_GRAPH_AGENT_NODE' ) {
                  // Agent State lives on the MAIN canvas — contextField/
                  // outputField aren't pins, so there's no wire to look up
                  // (unlike sourceNode/branches above, which resolve from
                  // real SOURCE_IN/BRANCH_OUT wires). canvasBuilder.jsx
                  // resolves the connected AG_GRAPH_STATE instance from
                  // circuit._parentCircuit and passes its settings down as
                  // the agentStateSettings prop — same established pattern
                  // as gatewayLabel/flowNodeTypes.
                  const stateSettings = agentStateSettings;

                  let fieldOptions = [];
                  if (stateSettings) {
                    const rawFields = stateSettings.fields;
                    const baseNames = Array.isArray(rawFields)
                      ? rawFields.map(f => (typeof f === 'string' ? f : f?.name)).filter(Boolean)
                      : (typeof rawFields === 'string'
                          ? (rawFields.match(/^\[([\s\S]*)\]$/)?.[1] || '')
                              .split(',')
                              .map(s => s.trim().replace(/^['"]|['"]$/g, ''))
                              .filter(Boolean)
                          : []);
                    fieldOptions = [
                      ...baseNames,
                      ...(stateSettings.includeSessionId ? ['session_id'] : []),
                      ...(stateSettings.includeUserId ? ['user_id'] : []),
                    ];
                  }

                  // NEW: a node wired as a Send Dispatcher's TARGET_OUT runs with a
                  // scoped {taskKey, taskKey_id} dict ONLY — not the full Agent
                  // State (see AG_GRAPH_SEND_DISPATCH's dispatch_fn) — so its
                  // contextField legitimately needs to read that key, not a
                  // declared Agent State field.
                  if (settingKey === 'contextField') {
                    // FLOW_IN allows multiConnect, so scan every incoming wire
                    // (not just the first) for one that comes from a Send
                    // Dispatcher specifically.
                    const incomingFlowWires = circuit?.connections?.filter(c =>
                      c.to?.componentInstanceId === component.instanceId && c.to?.pinName === 'FLOW_IN'
                    ) || [];
                    let dispatcherComp = null;
                    for (const w of incomingFlowWires) {
                      const srcComp = circuit?.components?.find(c => c.instanceId === w.from?.componentInstanceId);
                      if (srcComp?.componentId === 'AG_GRAPH_SEND_DISPATCH') {
                        dispatcherComp = srcComp;
                        break;
                      }
                    }
                    const taskKey = dispatcherComp?.communicationConfigs?.sendDispatchConfig?.settings?.taskKey;
                    if (taskKey) {
                      fieldOptions = [...fieldOptions, taskKey, `${taskKey}_id`];
                    }
                  }

                  const label = settingKey === 'contextField' ? 'Context Field' : settingKey === 'outputField' ? 'Output Field' : 'Grounding Field (optional)';
                  const isOptional = settingKey === 'groundingField';

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">{label}:</label>
                      {fieldOptions.length > 0 ? (
                        <select
                          className="config-input"
                          value={fieldOptions.includes(value) ? value : ''}
                          onChange={(e) => handleProtocolSettingChange(protocolName, settingKey, e.target.value)}
                        >
                          <option value="" disabled={!isOptional}>
                            {isOptional ? '— none (disabled) —' : '— select a field —'}
                          </option>
                          {fieldOptions.map(f => <option key={f} value={f}>{f}</option>)}
                        </select>
                      ) : (
                        <>
                          <input
                            type="text"
                            className="config-input"
                            value={value}
                            disabled
                            style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
                          />
                          <small className="config-hint" style={{ color: '#ef4444' }}>
                            No Agent State connected to this graph yet — add one on the main canvas first.
                          </small>
                        </>
                      )}
                      {fieldOptions.length > 0 && value && !fieldOptions.includes(value) && (
                        <small className="config-hint" style={{ color: '#ef4444' }}>
                          Current value "{value}" isn't in the connected Agent State's fields — select a valid one.
                        </small>
                      )}
                    </div>
                  );
                }

                // if (settingKey === 'systemPrompt' || settingKey === 'humanTemplate') {
                //   return (
                //     <div key={settingKey} className="config-form-group">
                //       <label className="config-label">
                //         {settingKey.replace(/([A-Z])/g, ' $1').trim()}:
                //       </label>
                //       <textarea
                //         rows={3}
                //         value={value}
                //         maxLength={rules?.maxLength}
                //         onChange={(e) => {
                //           handleProtocolSettingChange(protocolName, settingKey, e.target.value);
                //           resizeTextarea(e.target);
                //         }}
                //         ref={registerTextareaRef(`${protocolName}-${settingKey}`)}
                //         placeholder={settingKey === 'systemPrompt' ? 'e.g. You are a helpful assistant.' : 'e.g. What is a real fact about...'}
                //         className="config-textarea-autosize"
                //       />
                //     </div>
                //   );
                // }

                if (MULTILINE_FIELDS.includes(settingKey)) {
                  const placeholder =
                    settingKey === 'systemPrompt' ? 'e.g. You are a helpful assistant.' :
                    settingKey === 'humanTemplate' ? 'e.g. What is a real fact about...' :
                    `Enter ${settingKey}`;

                  return (
                    <div key={settingKey} className="config-form-group">
                      <label className="config-label">
                        {settingKey.replace(/([A-Z])/g, ' $1').trim()}:
                      </label>
                      <textarea
                        className="config-textarea-autosize"
                        value={value}
                        maxLength={rules?.maxLength}
                        ref={registerTextareaRef(`${protocolName}-${settingKey}`)}
                        onChange={(e) => {
                          handleProtocolSettingChange(protocolName, settingKey, e.target.value);
                          resizeTextarea(e.target);
                        }}
                        placeholder={placeholder}
                      />
                    </div>
                  );
                }

                return (
                  <div key={settingKey} className="config-form-group">
                      <label className="config-label">
                          {settingKey.replace(/([A-Z])/g, ' $1').trim()}:
                      </label>

                      {/* ENUM — render as dropdown */}
                      {enumOptions ? (
                          <select
                              className="config-input"
                              value={value}
                              disabled={isReadonly}
                              onChange={(e) => {
                                  const val = settingType === 'number' 
                                      ? Number(e.target.value) 
                                      : e.target.value;
                                  handleProtocolSettingChange(protocolName, settingKey, val);
                              }}
                          >
                              {enumOptions.map(opt => (
                                  <option key={opt} value={opt}>{opt}</option>
                              ))}
                          </select>

                      ) : settingType === 'boolean' ? (
                          <input
                              type="checkbox"
                              checked={value}
                              onChange={(e) => handleProtocolSettingChange(protocolName, settingKey, e.target.checked)}
                              className="config-checkbox"
                          />

                      ) : settingType === 'number' ? (
                          <input
                              type="number"
                              value={value}
                              min={rules?.min}
                              max={rules?.max}
                              onChange={(e) => handleProtocolSettingChange(protocolName, settingKey, Number(e.target.value))}
                              className="config-input"
                          />

                      ) : (
                          <input
                              type="text"
                              value={value}
                              maxLength={rules?.maxLength}
                              onChange={(e) => handleProtocolSettingChange(protocolName, settingKey, e.target.value)}
                              className="config-input"
                              placeholder={`Enter ${settingKey}`}
                          />
                      )}

                      {/* Show validation hint */}
                      {rules?.min !== undefined && rules?.max !== undefined && settingType === 'number' && (
                          <small className="config-hint">Range: {rules.min} — {rules.max}</small>
                      )}
                      {rules?.pattern && (
                          <small className="config-hint">Format: {rules.pattern}</small>
                      )}
                  </div>
                );
            })}
        </div>
    );
  };
  
  const renderAzureIoTHubSettings = () => {
    const azureSettings = config.communicationConfigs?.azureIoT?.settings || {};
    const devices = azureSettings.devices || [];
    const activeDeviceId = azureSettings.activeDeviceId || '';
    const sdkType = azureSettings.sdkType || 'AzureIoTSDK';
    const protocol = azureSettings.protocol || 'MQTT';
    const hostName = azureSettings.hostName || '';

    return (
      <div className="config-settings-form">
        {/* SDK Type Selection */}
        <div className="config-form-group">
          <label className="config-label">SDK / Library:</label>
          <select
            className="config-input"
            value={sdkType}
            onChange={(e) => handleProtocolSettingChange('azureIoT', 'sdkType', e.target.value)}
          >
            <option value="AzureIoTSDK">Azure IoT C SDK (Official) - Full Features</option>
            <option value="ESP32MQTTClient">ESP32MQTTClient (Lightweight)</option>
            <option value="PubSubClient">PubSubClient (Manual MQTT)</option>
          </select>
          <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', display: 'block' }}>
            {sdkType === 'AzureIoTSDK' && '✅ Recommended: Full Azure IoT Hub features (C2D, Direct Methods, Device Twin)'}
            {sdkType === 'ESP32MQTTClient' && '⚡ Lightweight: Optimized for ESP32, MQTT only'}
            {sdkType === 'PubSubClient' && '🔧 Manual: Generic MQTT, requires custom implementation'}
          </small>
        </div>

        {/* Protocol Selection */}
        <div className="config-form-group">
          <label className="config-label">Protocol:</label>
          <select
            className="config-input"
            value={protocol}
            onChange={(e) => handleProtocolSettingChange('azureIoT', 'protocol', e.target.value)}
            disabled={sdkType !== 'AzureIoTSDK'}
          >
            <option value="MQTT">MQTT</option>
            {sdkType === 'AzureIoTSDK' && (
              <>
                <option value="MQTT_WS">MQTT over WebSockets</option>
                <option value="AMQP">AMQP</option>
                <option value="AMQP_WS">AMQP over WebSockets</option>
                <option value="HTTP">HTTP</option>
              </>
            )}
          </select>
          {sdkType !== 'AzureIoTSDK' && (
            <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
              Only MQTT is supported with {sdkType}
            </small>
          )}
        </div>

        {/* IoT Hub Name (split into two parts) */}
        <div className="config-form-group">
          <label className="config-label">Host Name:</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
            <input
              type="text"
              className="config-input"
              value={hostName.replace('.azure-devices.net', '')}
              onChange={(e) => {
                const hubName = e.target.value.replace('.azure-devices.net', ''); // Remove if user pastes full hostname
                handleProtocolSettingChange('azureIoT', 'hostName', `${hubName}.azure-devices.net`);
              }}
              placeholder="my-iot-hub"
              style={{ 
                borderTopRightRadius: '0', 
                borderBottomRightRadius: '0',
                flex: '1'
              }}
            />
            <div style={{
              padding: '8px 12px',
              backgroundColor: '#f5f5f5',
              border: '1px solid #ddd',
              borderLeft: 'none',
              borderTopRightRadius: '4px',
              borderBottomRightRadius: '4px',
              color: '#666',
              fontSize: '0.95em',
              whiteSpace: 'nowrap'
            }}>
              .azure-devices.net
            </div>
          </div>
          <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', display: 'block' }}>
            Get from: Azure Portal → IoT Hub → Overview → Hostname (without .azure-devices.net suffix)
          </small>
        </div>

        {/* Devices Management */}
        <div className="config-form-group" style={{ marginTop: '20px' }}>
          <label className="config-label">Devices ({devices.length}):</label>
          
          {/* Device List */}
          <div style={{ marginTop: '10px', marginBottom: '10px' }}>
            {devices.length === 0 ? (
              <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '4px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#666' }}>No devices configured</p>
              </div>
            ) : (
              devices.map((device, index) => (
                <div
                  key={index}
                  style={{
                    padding: '12px',
                    marginBottom: '8px',
                    backgroundColor: device.enabled ? '#f0f9ff' : '#f5f5f5',
                    border: activeDeviceId === device.deviceId ? '2px solid #0066cc' : '1px solid #ddd',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                      {device.deviceId}
                      {activeDeviceId === device.deviceId && (
                        <span style={{ marginLeft: '8px', padding: '2px 8px', backgroundColor: '#0066cc', color: 'white', borderRadius: '3px', fontSize: '0.8em' }}>
                          ACTIVE
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: '0.85em', color: '#666' }}>
                      Key: {device.deviceKey.substring(0, 20)}...
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleToggleDevice(device.deviceId)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '0.85em',
                        backgroundColor: device.enabled ? '#28a745' : '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      {device.enabled ? '✓ Enabled' : '✗ Disabled'}
                    </button>
                    {activeDeviceId !== device.deviceId && (
                      <button
                        type="button"
                        onClick={() => handleSetActiveDevice(device.deviceId)}
                        style={{
                          padding: '4px 8px',
                          fontSize: '0.85em',
                          backgroundColor: '#0066cc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          cursor: 'pointer'
                        }}
                      >
                        Set Active
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleEditDevice(device)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '0.85em',
                        backgroundColor: '#ffc107',
                        color: '#000',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRemoveDevice(device.deviceId)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '0.85em',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer'
                      }}
                    >
                      ✖
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add Device Button */}
          <button
            type="button"
            onClick={() => setShowDeviceModal(true)}
            style={{
              width: '100%',
              padding: '10px',
              backgroundColor: '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            ➕ Add Device
          </button>
        </div>

        {/* Active Device Selection */}
        {devices.length > 0 && (
          <div className="config-form-group">
            <label className="config-label">Active Device (for code generation):</label>
            <select
              className="config-input"
              value={activeDeviceId}
              onChange={(e) => handleSetActiveDevice(e.target.value)}
            >
              <option value="">-- Select Active Device --</option>
              {devices.filter(d => d.enabled).map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.deviceId}
                </option>
              ))}
            </select>
            <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
              This device will be used for code generation
            </small>
          </div>
        )}
      </div>
    );
  };

  const renderPinSettings = (pinName, pinConfig) => {
    console.log('🔧 renderPinSettings called:', { pinName, category: component.category, settingKeys: Object.keys(pinConfig?.settings || {}) });
    const settings = pinConfig.settings || {};
    const currentPin = config.pinDetails.find(p => p.pinName === pinName);
    const isEnabled = currentPin?.enabled || false;

    if (!isEnabled) return null;

    console.log('🔧 wiredSource check — category:', component.category, 'calling getWiredSourceForPin:', component.category === 'nr_dashboard');
    const wiredSource = component.category === 'nr_dashboard' ? getWiredSourceForPin(pinName) : null;

    return (
      <div className="config-settings-form">
        {component.category === 'nr_dashboard' && (
          <div style={{
            padding: '10px 12px', marginBottom: '12px', borderRadius: '6px',
            backgroundColor: wiredSource ? '#ecfdf5' : '#fef3c7',
            border: `1px solid ${wiredSource ? '#10b981' : '#f59e0b'}`,
            fontSize: '0.85em',
          }}>
            {wiredSource ? (
              <>
                <strong>🔗 Connected from:</strong> {wiredSource.label}
                {wiredSource.nodeRedType && <> ({wiredSource.nodeRedType})</>} — pin "{wiredSource.pinName}"
                <div style={{ marginTop: '6px' }}>
                  {wiredSource.addressing === 'index' ? (
                    <>This node emits an array of values. Set "Field" to <code>{wiredSource.exampleField}</code> (or <code>payload[1]</code>, etc.) to pick one.</>
                  ) : (
                    <>This node emits a named object. Set "Field" to <code>{wiredSource.exampleField}</code>, matching whatever key your function returns.</>
                  )}
                </div>
              </>
            ) : (
              <>⚠️ Not connected yet. Wire this pin from a Function, Modbus Read, or other output node before configuring what to display — otherwise this widget has nothing to show.</>
            )}
          </div>
        )}
        {Object.keys(settings).map(settingKey => {
          const value = currentPin?.settings?.[settingKey] ?? settings[settingKey];
          const settingType = typeof settings[settingKey];

          // Handle array types (like topics array)
          if (Array.isArray(settings[settingKey])) {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">
                  {settingKey.replace(/([A-Z])/g, ' $1').trim()}:
                </label>
                <input
                  type="text"
                  value={Array.isArray(value) ? value.join(', ') : value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value.split(',').map(s => s.trim()))}
                  className="config-input"
                  placeholder="Comma-separated values"
                />
              </div>
            );
          }

          // Special case for payloadFormat (readonly)
          if (settingKey === 'payloadFormat') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">
                  {settingKey.replace(/([A-Z])/g, ' $1').trim()}:
                </label>
                <input
                  type="text"
                  value={value}
                  className="config-input"
                  disabled={true}
                  style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
                  Format is fixed to JSON for code generation compatibility
                </small>
              </div>
            );
          }

          // ✅ Special case for qos (dropdown with 0, 1, 2)
          if (settingKey === 'qos') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">QoS (Quality of Service):</label>
                <select
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                >
                  <option value={0}>0 - At Most Once</option>
                  <option value={1}>1 - At Least Once</option>
                  <option value={2}>2 - Exactly Once</option>
                </select>
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', display: 'block' }}>
                  QoS 0: Fastest | QoS 1: Most common | QoS 2: Guaranteed
                </small>
              </div>
            );
          }

          // ✅ Special case for messagingInterval (Azure IoT Hub TELEMETRY_IN)
          if (settingKey === 'messagingInterval') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Telemetry Interval (ms):</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={1000}
                  max={3600000}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', display: 'block' }}>
                  How often to send telemetry (1000ms = 1 second)
                </small>
              </div>
            );
          }

          // ✅ Special case for batchSize (Azure IoT Hub TELEMETRY_IN)
          if (settingKey === 'batchSize') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Batch Size:</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={1}
                  max={100}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
                  Number of messages to batch (1-100)
                </small>
              </div>
            );
          }

          // ✅ Special case for contentType (Azure IoT Hub TELEMETRY_IN)
          if (settingKey === 'contentType') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Content Type:</label>
                <select
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                >
                  <option value="application/json">application/json</option>
                  <option value="text/plain">text/plain</option>
                  <option value="application/octet-stream">application/octet-stream</option>
                </select>
              </div>
            );
          }

          // ✅ Special case for contentEncoding (Azure IoT Hub TELEMETRY_IN)
          if (settingKey === 'contentEncoding') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Content Encoding:</label>
                <select
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                >
                  <option value="utf-8">utf-8</option>
                  <option value="utf-16">utf-16</option>
                  <option value="utf-32">utf-32</option>
                </select>
              </div>
            );
          }

          // ✅ Special case for priority (Azure IoT Hub TELEMETRY_IN)
          if (settingKey === 'priority') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Message Priority:</label>
                <select
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                >
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </select>
              </div>
            );
          }

          // ✅ Special case for c2dTimeout (Azure IoT Hub COMMAND_OUT)
          if (settingKey === 'c2dTimeout') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">C2D Timeout (ms):</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={1000}
                  max={300000}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
                  Timeout for Cloud-to-Device messages (1000-300000ms)
                </small>
              </div>
            );
          }

          // ✅ Special case for directMethodTimeout (Azure IoT Hub COMMAND_OUT)
          if (settingKey === 'directMethodTimeout') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Direct Method Timeout (ms):</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={5000}
                  max={300000}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
                  Timeout for Direct Methods (5000-300000ms)
                </small>
              </div>
            );
          }

          // ✅ Special case for updateInterval (Azure IoT Hub TWIN_UPDATE)
          if (settingKey === 'updateInterval') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Twin Update Interval (ms):</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={10000}
                  max={86400000}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
                  How often to sync device twin (10000ms = 10 seconds)
                </small>
              </div>
            );
          }

          // ✅ Special case for statusLedPin (Azure IoT Hub STATUS)
          if (settingKey === 'statusLedPin') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Status LED Pin:</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={0}
                  max={39}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666' }}>
                  GPIO pin for connection status LED (0-39 for ESP32)
                </small>
              </div>
            );
          }

          // ✅ Special case for invertLogic (Azure IoT Hub STATUS)
          if (settingKey === 'invertLogic') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.checked)}
                    className="config-checkbox"
                  />
                  <span style={{ marginLeft: '8px' }}>Invert LED Logic</span>
                </label>
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666', display: 'block', marginLeft: '24px' }}>
                  Check if LED is active-LOW (common cathode)
                </small>
              </div>
            );
          }

          // Special case: protocol selector (Node-RED Gateway HARDWARE_IN and CLOUD_OUT)
          if (settingKey === 'protocol') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Protocol:</label>
                <select
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                >
                  <option value="mqtt">MQTT</option>
                  <option value="opcua">OPC-UA</option>
                  <option value="modbus">Modbus TCP</option>
                  <option value="http">HTTP</option>
                </select>
                <small className="config-hint">
                  Select the protocol this port uses to send/receive data
                </small>
              </div>
            );
          }

          // Special case: Modbus register start address (FBD_COMM_MODBUS PROTOCOL_CONFIG)
          if (settingKey === 'startRegister') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Start Register:</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={40001}
                  max={49999}
                />
                <small className="config-hint">Modbus holding register range: 40001–49999</small>
              </div>
            );
          }

          // Special case: scaling factor (FBD_COMM_MODBUS PROTOCOL_CONFIG)
          if (settingKey === 'scalingFactor') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Scaling Factor:</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={1}
                  max={1000}
                />
                <small className="config-hint">Register value = real value × scaling factor (e.g. 10 → 23.5°C stored as 235)</small>
              </div>
            );
          }

          // Special case: OPC-UA namespace index (FBD_COMM_OPCUA PROTOCOL_CONFIG)
          if (settingKey === 'namespace') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Namespace Index:</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={0}
                />
                <small className="config-hint">OPC-UA namespace index this server publishes nodes under</small>
              </div>
            );
          }

          // Special case: OPC-UA starting node index (FBD_COMM_OPCUA PROTOCOL_CONFIG)
          if (settingKey === 'startIndex') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Start Node Index:</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={1}
                />
              </div>
            );
          }

          // Special case: MQTT topic (FBD_COMM_MQTT PROTOCOL_CONFIG)
          if (settingKey === 'topic') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">MQTT Topic:</label>
                <input
                  type="text"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                  placeholder="e.g. plc/telemetry"
                />
                <small className="config-hint">Topic this block publishes connected inputs to</small>
              </div>
            );
          }

          // Special case: EtherNet/IP slot (FBD_COMM_ETHERNETIP PROTOCOL_CONFIG)
          if (settingKey === 'slot') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Controller Slot:</label>
                <input
                  type="number"
                  className="config-input"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  min={0}
                />
                <small className="config-hint">Allen-Bradley backplane slot number for this controller</small>
              </div>
            );
          }

          // Special case: fbdType — read-only port type tag (REAL/BOOL), shown for awareness only
          if (settingKey === 'fbdType') {
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">Port Type:</label>
                <span style={{ fontSize: '0.85rem', color: '#94a3b8', fontStyle: 'italic' }}>
                  {value} (fixed by block type)
                </span>
              </div>
            );
          }

          // Special case: pin selector for FBD I/O modules (FBD_AI_MODULE / FBD_DI_MODULE)
          if (settingKey === 'pin') {
            const options = pinConfig?.validationRules?.pin?.enum || [];
            return (
              <div key={settingKey} className="config-form-group">
                <label className="config-label">PLC Pin:</label>
                {options.length > 0 ? (
                  <select
                    className="config-input"
                    value={value}
                    onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                  >
                    {options.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    className="config-input"
                    value={value}
                    onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                  />
                )}
                <small className="config-hint">Which physical PLC pin this block reads from</small>
              </div>
            );
          }

          return (
            <div key={settingKey} className="config-form-group">
              <label className="config-label">
                {settingKey.replace(/([A-Z])/g, ' $1').trim()}:
              </label>
              {settingType === 'boolean' ? (
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.checked)}
                  className="config-checkbox"
                />
              ) : settingType === 'number' ? (
                <input
                  type="number"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, parseInt(e.target.value))}
                  className="config-input"
                />
              ) : (
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handlePinSettingChange(pinName, settingKey, e.target.value)}
                  className="config-input"
                  placeholder={`Enter ${settingKey}`}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={`config-modal-overlay 
      ${(validationErrors.length > 0 || validationWarnings.length > 0) ? 'dimmed' : ''}`}>
      <div className="config-modal-content" style={{backgroundColor: 'azure', minWidth: '500px'}}>
        {/* Header */}
        <div className="config-modal-header">
          <div>
            <h3>⚙️ Configure {component.componentName || component.name}</h3>
            <p className="config-subtitle">{component.instanceId}</p>
          </div>
          <button className="config-close-btn" onClick={handleClose}>✖</button>
        </div>

        {/* config modal tabs */}
        <div className="config-tabs">
          {/* Protocol / Configuration Tab — hidden for PLCs (comm protocol
              selection now lives on FBD comm blocks, not the PLC itself) */}
          {component.subCanvasType !== 'fbd' && (
              <button
                  className={`config-tab ${activeTab === 'protocols' ? 'active' : ''}`}
                  onClick={() => setActiveTab('protocols')}
              >
                  {domainConfig.protocolTabLabel}
                  {availableProtocols.length > 0 && (
                      <span className="config-tab-badge">
                          {availableProtocols.length + (domainConfig.showWireless ? Object.keys(component.wirelessConfigs || {}).length : 0)}
                      </span>
                  )}
              </button>
          )}

          {/* Data Pins / Software Pins Tab */}
          {Object.keys(availablePins).length > 0 && (
              <button
                  className={`config-tab ${activeTab === 'pins' ? 'active' : ''}`}
                  onClick={() => setActiveTab('pins')}
              >
                  {domainConfig.pinTabLabel}
                  <span className="config-tab-badge">{Object.keys(availablePins).length}</span>
              </button>
          )}

          {/* Flow Canvas Tab — only for Node-RED Gateway */}
          {component.isFlowEngine && domain !== 'AgenticAI' && (
              <button
                  className={`config-tab ${activeTab === 'flow' ? 'active' : ''}`}
                  onClick={() => setActiveTab('flow')}
                  style={{ borderColor: '#8F0000', color: activeTab === 'flow' ? '#fff' : '#8F0000',
                          background: activeTab === 'flow' ? '#8F0000' : 'transparent' }}
              >
                  🔴 Flow Settings
                  <span className="config-tab-badge" style={{ background: '#8F0000' }}>
                      {component.nodeRedFlow?.nodes?.length || 0}
                  </span>
              </button>
          )}

          {/* FBD Program Tab — only for PLC components */}
          {component.subCanvasType === 'fbd' && (
              <button
                  className={`config-tab ${activeTab === 'fbd' ? 'active' : ''}`}
                  onClick={() => setActiveTab('fbd')}
                  style={{ borderColor: '#009999', color: activeTab === 'fbd' ? '#fff' : '#009999',
                          background: activeTab === 'fbd' ? '#009999' : 'transparent' }}
              >
                  🏭 FBD Program
                  <span className="config-tab-badge" style={{ background: '#009999' }}>
                      {component.plcFlow?.nodes?.length || 0}
                  </span>
              </button>
          )}
        </div>

        {/* Body */}
        <div className="config-modal-body">
          {/* Protocols/Configuration Tab */}
          {activeTab === 'protocols' && component.subCanvasType !== 'fbd' && (
            <div className="config-section">
              {/* Wireless Configs (WiFi, Bluetooth, etc.) — IoT only */}
              {domainConfig.showWireless && component.wirelessConfigs && Object.keys(component.wirelessConfigs).length > 0 && (
                  <>
                      {Object.keys(component.wirelessConfigs).map((wirelessType) => {
                          const wirelessConfig = component.wirelessConfigs[wirelessType] || {};
                          const isEnabled = config.wirelessConfigs?.[wirelessType]?.enabled || false;
                          return (
                              <div key={wirelessType} className="config-protocol-item">
                                  <div className="config-protocol-header">
                                      <label className="config-protocol-label">
                                          <input
                                              type="checkbox"
                                              checked={isEnabled}
                                              onChange={() => handleWirelessToggle(wirelessType)}
                                              className="config-checkbox-large"
                                          />
                                          <span className="config-protocol-name">
                                              {wirelessType.toUpperCase()}
                                          </span>
                                      </label>
                                  </div>
                                  {renderWirelessSettings(wirelessType, wirelessConfig)}
                              </div>
                          );
                      })}
                  </>
              )}

              {/* Communication Protocols / AI Configuration */}
              {availableProtocols.length > 0 ? (
                  <>
                      {availableProtocols.map(protocolName => {
                          const protocolConfig = protocolConfigs[protocolName] || {};
                          const isEnabled = config.communicationConfigs[protocolName]?.enabled || false;
                          return (
                              <div key={protocolName} className="config-protocol-item">
                                  <div className="config-protocol-header">
                                      <label className="config-protocol-label">
                                          <input
                                              type="checkbox"
                                              checked={isEnabled}
                                              onChange={() => handleProtocolToggle(protocolName)}
                                              className="config-checkbox-large"
                                          />
                                          <span className="config-protocol-name">
                                              {protocolName.replace(/([A-Z])/g, ' $1').trim().toUpperCase()}
                                          </span>
                                      </label>
                                  </div>
                                  {renderProtocolSettings(protocolName, protocolConfig)}
                              </div>
                          );
                      })}
                  </>
              ) : (
                  <div className="config-empty">
                      <p>ℹ️ {domainConfig.emptyProtocolMessage}</p>
                  </div>
              )}
            </div>
          )}

          {/* Software Pins Tab */}
          {activeTab === 'pins' && (
            <div className="config-section">
              
              {Object.keys(availablePins).length === 0 ? (
                <div className="config-empty">
                  {/* <p>ℹ️ This component does not have software pins</p> */}
                  <p>ℹ️ {domainConfig.emptyPinMessage}</p>
                </div>
              ) : (
                Object.keys(availablePins).map(pinName => {
                  const pinConfig = availablePins[pinName];
                  const currentPin = config.pinDetails.find(p => p.pinName === pinName);
                  const isEnabled = currentPin?.enabled || false;

                  return (
                    <div key={pinName} className="config-pin-item">
                      <div className="config-pin-header">
                        <label className="config-pin-label">
                          <input
                            type="checkbox"
                            checked={isEnabled}
                            onChange={() => handlePinToggle(pinName)}
                            className="config-checkbox-large"
                          />
                          <div>
                            <span className="config-pin-name">{pinName}</span>
                            <span className="config-pin-direction">
                              {pinConfig.direction === 'input' && '⬇️ Input'}
                              {pinConfig.direction === 'output' && '⬆️ Output'}
                              {pinConfig.direction === 'bidirectional' && '⬍ Bidirectional'}
                            </span>
                          </div>
                        </label>
                        {pinConfig.description && (
                          <p className="config-pin-description">{pinConfig.description}</p>
                        )}
                      </div>
                      {renderPinSettings(pinName, pinConfig)}
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Flow Canvas Settings Tab — Node-RED Gateway only */}
          {activeTab === 'flow' && component.isFlowEngine && (
            <div className="config-section">

              {/* Flow summary banner */}
              <div className="config-flow-summary">
                <div className="config-flow-stat">
                  <span>📦 Nodes</span>
                  <strong>{component.nodeRedFlow?.nodes?.length || 0}</strong>
                </div>
                <div className="config-flow-stat">
                  <span>🔗 Wires</span>
                  <strong>{component.nodeRedFlow?.wires?.length || 0}</strong>
                </div>
                <div className="config-flow-stat">
                  <span>📥 Inputs</span>
                  <strong>
                    {component.nodeRedFlow?.nodes?.filter(n => n.category === 'nr_input').length || 0}
                  </strong>
                </div>
                <div className="config-flow-stat">
                  <span>📤 Outputs</span>
                  <strong>
                    {component.nodeRedFlow?.nodes?.filter(n => n.category === 'nr_output').length || 0}
                  </strong>
                </div>
              </div>

              {/* Flow label */}
              <div className="config-form-group">
                <label className="config-label">Flow Label</label>
                <input
                  type="text"
                  className="config-input"
                  value={flowConfig.flowLabel}
                  onChange={e => setFlowConfig(prev => ({ ...prev, flowLabel: e.target.value }))}
                  placeholder="e.g. Temperature Monitoring Flow"
                />
              </div>

              {/* Flow description */}
              <div className="config-form-group">
                <label className="config-label">Flow Description</label>
                <textarea
                  className="config-input"
                  rows={3}
                  value={flowConfig.flowDescription}
                  onChange={e => setFlowConfig(prev => ({ ...prev, flowDescription: e.target.value }))}
                  placeholder="Describe what this flow does..."
                />
              </div>

              {/* Deploy mode */}
              <div className="config-form-group">
                <label className="config-label">Deploy Mode</label>
                <select
                  className="config-input"
                  value={flowConfig.deployMode}
                  onChange={e => setFlowConfig(prev => ({ ...prev, deployMode: e.target.value }))}
                >
                  <option value="full">Full — redeploy all nodes</option>
                  <option value="modified">Modified — only changed nodes</option>
                  <option value="nodes">Nodes — redeploy selected nodes</option>
                </select>
                <small className="config-hint">
                  "Modified" is recommended for live IIoT deployments to avoid
                  dropping active connections.
                </small>
              </div>

              {/* Auto-start toggle */}
              <div className="config-protocol-item">
                <div className="config-protocol-header">
                  <label className="config-protocol-label">
                    <input
                      type="checkbox"
                      checked={flowConfig.autoStart}
                      onChange={e => setFlowConfig(prev => ({ ...prev, autoStart: e.target.checked }))}
                      className="config-checkbox-large"
                    />
                    <span className="config-protocol-name">AUTO START</span>
                  </label>
                </div>
                <p className="config-pin-description">
                  Start the flow automatically when the Node-RED container starts.
                </p>
              </div>

              {/* Debug toggle */}
              <div className="config-protocol-item">
                <div className="config-protocol-header">
                  <label className="config-protocol-label">
                    <input
                      type="checkbox"
                      checked={flowConfig.debug}
                      onChange={e => setFlowConfig(prev => ({ ...prev, debug: e.target.checked }))}
                      className="config-checkbox-large"
                    />
                    <span className="config-protocol-name">DEBUG MODE</span>
                  </label>
                </div>
                <p className="config-pin-description">
                  Enable the Debug node output panel in the dashboard page.
                  Disable in production to reduce overhead.
                </p>
              </div>

              {/* Node list — read only summary */}
              {component.nodeRedFlow?.nodes?.length > 0 && (
                <div className="config-form-group" style={{ marginTop: '16px' }}>
                  <label className="config-label">Nodes in Flow</label>
                  <div className="config-flow-node-list">
                    {component.nodeRedFlow.nodes.map(node => (
                      <div key={node.instanceId} className="config-flow-node-row">
                        <span className={`config-flow-node-cat config-flow-node-cat--${node.category}`}>
                          {node.category === 'nr_input'      ? '📥' :
                           node.category === 'nr_processing' ? '⚙️' : '📤'}
                        </span>
                        <span className="config-flow-node-name">
                          {node.label || node.componentName}
                        </span>
                        <span className="config-flow-node-type">{node.nodeRedType}</span>
                        <span className={`config-flow-node-status ${node.enabled ? 'on' : 'off'}`}>
                          {node.enabled ? '● on' : '○ off'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

          {/* FBD Program Tab — PLC summary/launcher, mirrors the gateway's Flow tab */}
          {activeTab === 'fbd' && component.subCanvasType === 'fbd' && (
            <div className="config-section">

              {/* FBD summary banner */}
              <div className="config-flow-summary">
                <div className="config-flow-stat">
                  <span>📦 Blocks</span>
                  <strong>{component.plcFlow?.nodes?.length || 0}</strong>
                </div>
                <div className="config-flow-stat">
                  <span>🔗 Wires</span>
                  <strong>{component.plcFlow?.wires?.length || 0}</strong>
                </div>
                <div className="config-flow-stat">
                  <span>📡 Comm Blocks</span>
                  <strong>
                    {component.plcFlow?.nodes?.filter(n =>
                      typeof n.componentId === 'string' && n.componentId.startsWith('FBD_COMM_')
                    ).length || 0}
                  </strong>
                </div>
              </div>

              {!component.plcFlow?.nodes?.length && (
                <div style={{
                  padding: '12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b',
                  borderRadius: '4px', marginBottom: '16px', fontSize: '0.9em'
                }}>
                  ⚠️ No FBD program yet. The PLC will simulate random values until
                  you build one. Click "Open FBD Canvas" below to get started.
                </div>
              )}

              {component.plcFlow?.nodes?.length > 0 &&
               !component.plcFlow.nodes.some(n =>
                 typeof n.componentId === 'string' && n.componentId.startsWith('FBD_COMM_')
               ) && (
                <div style={{
                  padding: '12px', backgroundColor: '#fef3c7', border: '1px solid #f59e0b',
                  borderRadius: '4px', marginBottom: '16px', fontSize: '0.9em'
                }}>
                  ⚠️ This program has no communication block (Modbus/OPC-UA/MQTT/
                  EtherNet-IP). Its output will never reach the gateway.
                </div>
              )}

              {/* Block list — read-only summary, same pattern as gateway's node list */}
              {component.plcFlow?.nodes?.length > 0 && (
                <div className="config-form-group" style={{ marginTop: '16px' }}>
                  <label className="config-label">Blocks in Program</label>
                  <div className="config-flow-node-list">
                    {component.plcFlow.nodes.map(node => (
                      <div key={node.instanceId} className="config-flow-node-row">
                        <span className="config-flow-node-cat">
                          {node.componentId?.startsWith('FBD_COMM_')  ? '📡' :
                           node.componentId?.startsWith('FBD_AI')     ? '📥' :
                           node.componentId?.startsWith('FBD_DI')     ? '📥' :
                           node.componentId?.startsWith('FBD_AO')     ? '📤' :
                           node.componentId?.startsWith('FBD_DO')     ? '📤' :
                           node.componentId?.startsWith('FBD_TON') ||
                           node.componentId?.startsWith('FBD_TOF') ||
                           node.componentId?.startsWith('FBD_CTU')    ? '⏱️' : '⚖️'}
                        </span>
                        <span className="config-flow-node-name">
                          {node.label || node.componentName}
                        </span>
                        <span className="config-flow-node-type">{node.componentId}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                className="config-save-btn"
                style={{ marginTop: '16px', width: '100%', background: '#009999' }}
                onClick={() => onOpenFbdEditor && onOpenFbdEditor(component)}
              >
                🏭 Open FBD Canvas
              </button>

            </div>
          )}

        </div>
      
        {/* Footer */}
        <div className="config-modal-footer">
          {validationWarnings.length > 0 ? (
            // Show "Close" button when there are warnings
            <>
              <button className="config-close-btn-alt" onClick={handleClose}>
                ✓ Close
              </button>
            </>
          ) : (
            // Show normal Cancel button when no warnings
            <>
              <button className="config-cancel-btn" onClick={handleClose}>
                Cancel
              </button>
              <button 
                className="config-save-btn" 
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? '⏳ Validating...' : '💾 Save Configuration'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ✅ ADD THIS: Device Add/Edit Modal */}
      {showDeviceModal && (
        <div className="config-modal-overlay" style={{ zIndex: 10000 }}>
          <div className="config-modal-content" style={{ maxWidth: '500px' }}>
            <div className="config-modal-header">
              <h3>{editingDevice ? '✏️ Edit Device' : '➕ Add New Device'}</h3>
              <button 
                className="config-close-btn" 
                onClick={() => {
                  setShowDeviceModal(false);
                  setNewDevice({ deviceId: '', deviceKey: '', enabled: true });
                  setEditingDevice(null); 
                }}
              >
                ✖
              </button>
            </div>

            <div className="config-modal-body">
              <div className="config-form-group">
                <label className="config-label">Device ID:</label>
                <input
                  type="text"
                  className="config-input"
                  value={newDevice.deviceId}
                  onChange={(e) => setNewDevice({ ...newDevice, deviceId: e.target.value })}
                  placeholder="e.g., sensor-001"
                  autoFocus
                  disabled={editingDevice !== null}
                  style={editingDevice ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' } : {}}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', display: 'block' }}>
                  {editingDevice 
                    ? '⚠️ Device ID cannot be changed when editing' 
                    : 'Get from: Azure Portal → IoT Hub → Devices → [Your Device] → Device ID'}
                </small>
              </div>

              <div className="config-form-group">
                <label className="config-label">Device Key (Primary or Secondary):</label>
                <textarea
                  className="config-input"
                  value={newDevice.deviceKey}
                  onChange={(e) => setNewDevice({ ...newDevice, deviceKey: e.target.value })}
                  placeholder="Base64 encoded key from Azure Portal"
                  rows={3}
                  style={{ fontFamily: 'monospace', fontSize: '0.9em' }}
                />
                <small className="form-text text-muted" style={{ fontSize: '0.85em', color: '#666', marginTop: '4px', display: 'block' }}>
                  Get from: Azure Portal → IoT Hub → Devices → [Your Device] → Primary Key
                </small>
              </div>

              <div className="config-form-group">
                <label className="config-label">
                  <input
                    type="checkbox"
                    checked={newDevice.enabled}
                    onChange={(e) => setNewDevice({ ...newDevice, enabled: e.target.checked })}
                    className="config-checkbox"
                  />
                  <span style={{ marginLeft: '8px' }}>Enable this device</span>
                </label>
              </div>

              <div style={{ 
                padding: '12px', 
                backgroundColor: '#e7f3ff', 
                borderLeft: '4px solid #0066cc', 
                borderRadius: '4px',
                marginTop: '15px'
              }}>
                <strong style={{ color: '#0066cc', fontSize: '14px' }}>💡 How to get credentials:</strong>
                <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '0.8em' }}>
                  <li>Go to Azure Portal</li>
                  <li>Navigate to your IoT Hub</li>
                  <li>Click "Devices" in left menu</li>
                  <li>Select your device (or create new)</li>
                  <li>Copy "Device ID" and "Primary Key"</li>
                </ol>
              </div>
            </div>

            <div className="config-modal-footer">
              <button 
                className="config-cancel-btn" 
                onClick={() => {
                  setShowDeviceModal(false);
                  setNewDevice({ deviceId: '', deviceKey: '', enabled: true });
                  setEditingDevice(null); 
                }}
              >
                Cancel
              </button>
              <button 
                className="config-save-btn" 
                onClick={handleAddDevice}
                disabled={!newDevice.deviceId || !newDevice.deviceKey}
              >
                {editingDevice ? '💾 Update Device' : '➕ Add Device'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogicBuilder && (
        <RouterLogicBuilderModal
          visible={showLogicBuilder}
          onClose={() => setShowLogicBuilder(false)}
          fieldOptions={getAgentStateFieldOptions()}
          branchOptions={getConditionalEdgeBranchLabels()}
          initialConditions={
            config.communicationConfigs[logicBuilderProtocol]?.settings?.routerConditions || []
          }
          initialElseBranch={
            config.communicationConfigs[logicBuilderProtocol]?.settings?.routerElseBranch || ''
          }
          onApply={(compiledLogic, conditions, elseBranch) => {
            handleProtocolSettingChange(logicBuilderProtocol, 'routerLogic', compiledLogic);
            handleProtocolSettingChange(logicBuilderProtocol, 'routerConditions', conditions);
            handleProtocolSettingChange(logicBuilderProtocol, 'routerElseBranch', elseBranch);
          }}
        />
      )}
    
    </div>
  );
};

export default ComponentConfiguration;